import ReminderTemplatesSettings from '../components/admin/ReminderTemplatesSettings';
import CollectionsDigestSettings from '../components/admin/CollectionsDigestSettings';
import PoTypesSettings from '../components/admin/PoTypesSettings';
import PoAccountingSettings from '../components/admin/PoAccountingSettings';
import PaymentMethodsSettings from '../components/admin/PaymentMethodsSettings';
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { UserPasswordActions } from '../components/users/UserPasswordActions';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Settings, Mail, Bell, DollarSign, Users, Bug, Save, RefreshCw, Hash, Plus, Trash2, Globe, Clock, Shield, UserPlus, KeyRound, Edit3, FileText, Eye, ChevronRight as ChevronRightIcon, Tv, Megaphone, HardHat, Pin, Cloud, MapPin, Truck } from 'lucide-react';
import ShippingPolicyPanel from '../components/ShippingPolicyPanel';
import { toast } from 'sonner';
import RichTextEditor from '../components/RichTextEditor';
import BomLinkedFileTemplatesPanel from '../components/BomLinkedFileTemplatesPanel';
import DangerZonePanel from '../components/DangerZonePanel';
import InboxRetentionPanel from '../components/InboxRetentionPanel';
import { Link2, ShoppingCart } from 'lucide-react';

const AdminSettingsPage = () => {
  const { api, user } = useAuth();
  const brandingCtx = useBranding();
  const branding = brandingCtx?.branding || {};
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('tab');
      return t || 'general';
    } catch { return 'general'; }
  });

  // System Settings
  const [systemSettings, setSystemSettings] = useState({
    smtp_enabled: false,
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    from_email: '',
    tutorial_enabled: true,
    maintenance_mode: false,
  });

  // iter81 — Cycle-count stale-days threshold (separate endpoint)
  const [cycleCountStaleDays, setCycleCountStaleDays] = useState(90);
  const [cycleCountSaving, setCycleCountSaving] = useState(false);

  // Labor Cost Settings
  const [laborSettings, setLaborSettings] = useState({
    default_hourly_rate: 25,
    overtime_multiplier: 1.5,
    currency: 'USD',
  });

  // User Rates
  const [users, setUsers] = useState([]);
  const [userRates, setUserRates] = useState({});

  // Serial Number Configs
  const [serialConfigs, setSerialConfigs] = useState([]);
  const [serialDialogOpen, setSerialDialogOpen] = useState(false);
  const [newSerialConfig, setNewSerialConfig] = useState({
    config_name: '',
    prefix: '',
    suffix: '',
    include_date: true,
    date_format: '%Y%m%d',
    padding_digits: 5
  });

  // Currency Settings
  const [currencySettings, setCurrencySettings] = useState({
    default_currency: 'USD',
    enabled_currencies: ['USD', 'EUR', 'GBP', 'CAD', 'AUD'],
    auto_update_rates: true,
    last_rate_update: null
  });
  const [currencyRates, setCurrencyRates] = useState([]);
  const [refreshingRates, setRefreshingRates] = useState(false);

  const AVAILABLE_CURRENCIES = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'MXN', name: 'Mexican Peso', symbol: '$' },
    { code: 'BRL', name: 'Brazilian Real', symbol: 'R$' },
  ];

  // Email Templates
  const [emailTemplates, setEmailTemplates] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState('low_stock_alert');
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateBody, setTemplateBody] = useState('');

  // iter47: Resend Email Settings
  const [emailConfig, setEmailConfig] = useState({
    api_key_override: '',
    api_key_override_masked: '',
    sender_email: 'onboarding@resend.dev',
    sender_name: '',
    reply_to: '',
    bcc_audit_email: '',
    cc_admins_on_critical: true,
    recipient_lists: {},
    enabled_purposes: {},
    env_resend_key_present: false,
  });
  const [emailPurposes, setEmailPurposes] = useState([]);
  const [emailHistory, setEmailHistory] = useState([]);
  const [emailHistoryLoading, setEmailHistoryLoading] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [recipientInputs, setRecipientInputs] = useState({}); // per-purpose draft input

  // iter48: per-purpose templates + history filters
  const [templates, setTemplates] = useState({}); // {purpose: {subject, body, variables, ...}}
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [previewing, setPreviewing] = useState({}); // {purpose: bool}
  const [previewResult, setPreviewResult] = useState({}); // {purpose: {subject, html}}
  const [historyFilters, setHistoryFilters] = useState({ purpose: '', status: '', start: '', end: '' });

  // Notification Settings
  const [notificationSettings, setNotificationSettings] = useState({
    email_notifications_enabled: true,
    low_stock_alerts: true,
    work_order_updates: true,
    bug_report_updates: true,
    daily_summary: false,
    weekly_report: true,
  });

  // --- iter31: Timecodes + Users + Roles state ---
  const [timecodes, setTimecodes] = useState([]);
  const [timecodeDialogOpen, setTimecodeDialogOpen] = useState(false);
  const [timecodeEditing, setTimecodeEditing] = useState(null);
  const [timecodeForm, setTimecodeForm] = useState({
    label: '', color: 'bg-slate-500', counts_toward_hours: true, is_payable: true, requires_work_order: false, active: true, sort_order: 100,
  });
  const COLOR_SWATCHES = [
    'bg-orange-500','bg-purple-500','bg-amber-500','bg-sky-500','bg-emerald-500',
    'bg-slate-500','bg-pink-500','bg-rose-500','bg-teal-500','bg-indigo-500','bg-gray-600','bg-red-500',
  ];

  const [roles, setRoles] = useState([]);
  const [permModules, setPermModules] = useState({ modules: [], actions: [] });
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [roleEditing, setRoleEditing] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: '', description: '', permissions: {}, active: true });

  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userEditing, setUserEditing] = useState(null);
  const [userForm, setUserForm] = useState({ email: '', full_name: '', role: 'Operator', password: '', is_active: true, birthday: '', hire_date: '' });

  // --- iter34: Board Pack branding state ---
  const [boardPack, setBoardPack] = useState({
    company_name: '', company_tagline: '', company_address: '', header_note: '',
    logo_data: '', contact_email: '', contact_phone: '',
    secondary_contact: '', fax: '', website: '', tax_id: '', facility_address: '', default_report_footer: '',
  });

  useEffect(() => {
    fetchAllSettings();
  }, []);

  // iter47: load email send history when Email tab is selected
  useEffect(() => {
    if (activeTab === 'email') {
      loadEmailHistory();
      loadTemplates();
    }
    if (activeTab === 'users') {
      // iter52: refresh pending list on tab open
      api().get('/users/pending').then(r => setPendingUsers(r.data || [])).catch(() => {});
    }
    if (activeTab === 'branding') {
      // iter54: load full branding config (admin endpoint)
      api().get('/admin/branding-settings').then(r => setBrandingForm(r.data)).catch(() => {});
    }
    if (activeTab === 'tvdisplay') {
      // iter55: load company news + safety
      loadTvDisplay();
    }
    if (activeTab === 'backups') {
      // iter60: load backup list + config
      loadBackups();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchAllSettings = async () => {
    setLoading(true);
    try {
      const [sysRes, laborRes, usersRes, ratesRes, templatesRes, notifRes, serialRes, currencyRes, currencyRatesRes, tcRes, rolesRes, modulesRes, bpRes, ecRes, epRes] = await Promise.all([
        api().get('/admin/settings'),
        api().get('/settings/labor-costs'),
        api().get('/users'),
        api().get('/settings/user-rates'),
        api().get('/admin/email-templates'),
        api().get('/admin/notification-settings'),
        api().get('/serial-configs').catch(() => ({ data: [] })),
        api().get('/currency/settings').catch(() => ({ data: { default_currency: 'USD', enabled_currencies: ['USD', 'EUR', 'GBP', 'CAD', 'AUD'], auto_update_rates: true } })),
        api().get('/currency/rates').catch(() => ({ data: [] })),
        api().get('/timecode-options').catch(() => ({ data: [] })),
        api().get('/roles').catch(() => ({ data: [] })),
        api().get('/roles/modules').catch(() => ({ data: { modules: [], actions: [] } })),
        api().get('/admin/board-pack-settings').catch(() => ({ data: {} })),
        api().get('/admin/email-settings').catch(() => ({ data: null })),
        api().get('/admin/email-settings/purposes').catch(() => ({ data: { purposes: [] } })),
      ]);
      
      setSystemSettings(sysRes.data);
      setLaborSettings(laborRes.data);
      setUsers(usersRes.data);
      setSerialConfigs(serialRes.data);
      setCurrencySettings(currencyRes.data);
      setCurrencyRates(currencyRatesRes.data);
      setTimecodes(tcRes.data || []);
      setRoles(rolesRes.data || []);
      setPermModules(modulesRes.data || { modules: [], actions: [] });
      setBoardPack({ ...boardPack, ...(bpRes.data || {}) });

      if (ecRes.data) setEmailConfig((prev) => ({ ...prev, ...ecRes.data }));
      setEmailPurposes((epRes.data && epRes.data.purposes) || []);

      // iter52: pending approvals (best-effort)
      try {
        const pendRes = await api().get('/users/pending');
        setPendingUsers(pendRes.data || []);
      } catch {}

      // iter53: auto-approve auth config (best-effort)
      try {
        const aRes = await api().get('/admin/auth-settings');
        if (aRes.data) setAuthConfig(aRes.data);
      } catch {}
      
      // Convert rates array to object
      const rates = {};
      ratesRes.data.forEach(r => { rates[r.user_id] = r.hourly_rate; });
      setUserRates(rates);
      
      setEmailTemplates(templatesRes.data);
      if (templatesRes.data.low_stock_alert) {
        setTemplateSubject(templatesRes.data.low_stock_alert.subject);
        setTemplateBody(templatesRes.data.low_stock_alert.body);
      }
      
      setNotificationSettings(notifRes.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSystemSettings = async () => {
    setSaving(true);
    try {
      await api().put('/admin/settings', systemSettings);
      toast.success('System settings saved');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // iter81 — cycle-count threshold load + save
  useEffect(() => {
    (async () => {
      try {
        const r = await api().get('/settings/cycle-count');
        if (r.data?.stale_days) setCycleCountStaleDays(Number(r.data.stale_days));
      } catch { /* fall back to 90 */ }
    })();
  }, []);

  const saveCycleCountSettings = async () => {
    setCycleCountSaving(true);
    try {
      await api().put('/settings/cycle-count', { stale_days: Number(cycleCountStaleDays) });
      toast.success(`Cycle-count threshold saved (${cycleCountStaleDays} days)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save threshold');
    } finally { setCycleCountSaving(false); }
  };

  const saveLaborSettings = async () => {
    setSaving(true);
    try {
      await api().put('/settings/labor-costs', laborSettings);
      toast.success('Labor settings saved');
    } catch (error) {
      toast.error('Failed to save labor settings');
    } finally {
      setSaving(false);
    }
  };

  const saveUserRate = async (userId, rate) => {
    try {
      await api().put(`/settings/user-rates/${userId}`, {
        user_id: userId,
        hourly_rate: parseFloat(rate),
        effective_date: new Date().toISOString().split('T')[0]
      });
      toast.success('User rate saved');
    } catch (error) {
      toast.error('Failed to save user rate');
    }
  };

  const saveEmailTemplate = async () => {
    setSaving(true);
    try {
      await api().put(`/admin/email-templates/${selectedTemplate}`, {
        subject: templateSubject,
        body: templateBody,
      });
      toast.success('Email template saved');
    } catch (error) {
      toast.error('Failed to save email template');
    } finally {
      setSaving(false);
    }
  };

  // iter47: Email/Resend handlers
  const saveEmailConfig = async () => {
    setSaving(true);
    try {
      const payload = {
        sender_email: emailConfig.sender_email,
        sender_name: emailConfig.sender_name,
        reply_to: emailConfig.reply_to,
        bcc_audit_email: emailConfig.bcc_audit_email,
        cc_admins_on_critical: emailConfig.cc_admins_on_critical,
        recipient_lists: emailConfig.recipient_lists,
        enabled_purposes: emailConfig.enabled_purposes,
      };
      // Only send api_key_override when user typed a non-empty value
      if (emailConfig.api_key_override && emailConfig.api_key_override.length > 0) {
        payload.api_key_override = emailConfig.api_key_override;
      }
      const res = await api().put('/admin/email-settings', payload);
      setEmailConfig((prev) => ({ ...prev, ...res.data, api_key_override: '' }));
      toast.success('Email settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save email settings');
    } finally {
      setSaving(false);
    }
  };

  const clearApiKeyOverride = async () => {
    setSaving(true);
    try {
      const res = await api().put('/admin/email-settings', { api_key_override: '' });
      setEmailConfig((prev) => ({ ...prev, ...res.data, api_key_override: '', api_key_override_masked: '' }));
      toast.success('API key override cleared — falling back to env');
    } catch (e) {
      toast.error('Failed to clear API key override');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testRecipient || !testRecipient.includes('@')) {
      toast.error('Enter a valid email address first');
      return;
    }
    setSendingTest(true);
    try {
      const res = await api().post('/admin/email-settings/test', { to: testRecipient });
      const status = res.data?.status;
      if (status === 'sent') {
        toast.success(`Test email sent · id=${(res.data.id || '').slice(0, 8)}…`);
      } else if (status === 'skipped') {
        toast.error(`Skipped: ${res.data?.reason || 'unknown'}`);
      } else {
        toast.error(`Resend error: ${res.data?.error || 'unknown'}`);
      }
      loadEmailHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  const loadEmailHistory = async () => {
    setEmailHistoryLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (historyFilters.purpose) params.append('purpose', historyFilters.purpose);
      if (historyFilters.status) params.append('status', historyFilters.status);
      if (historyFilters.start) params.append('start', historyFilters.start);
      if (historyFilters.end) params.append('end', historyFilters.end);
      const res = await api().get(`/admin/email-settings/history?${params.toString()}`);
      setEmailHistory(res.data?.rows || []);
    } catch (e) {
      // silent
    } finally {
      setEmailHistoryLoading(false);
    }
  };

  const exportHistoryCsv = async () => {
    try {
      const params = new URLSearchParams({});
      if (historyFilters.purpose) params.append('purpose', historyFilters.purpose);
      if (historyFilters.status) params.append('status', historyFilters.status);
      if (historyFilters.start) params.append('start', historyFilters.start);
      if (historyFilters.end) params.append('end', historyFilters.end);
      const res = await api().get(`/admin/email-settings/history.csv?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `email-history-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Email history exported');
    } catch (e) {
      toast.error('Failed to export history');
    }
  };

  // iter48: Templates handlers
  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await api().get('/admin/email-settings/templates');
      setTemplates(res.data?.templates || {});
    } catch (e) {
      // silent
    } finally {
      setTemplatesLoading(false);
    }
  };

  const updateTemplateField = (purpose, field, value) => {
    setTemplates({
      ...templates,
      [purpose]: { ...(templates[purpose] || {}), [field]: value },
    });
  };

  const saveTemplate = async (purpose) => {
    const tpl = templates[purpose] || {};
    setSaving(true);
    try {
      const res = await api().put(`/admin/email-settings/templates/${purpose}`, {
        subject: tpl.subject,
        body: tpl.body,
      });
      setTemplates({ ...templates, [purpose]: { ...tpl, ...res.data } });
      toast.success(`Template '${purpose}' saved`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const resetTemplate = async (purpose) => {
    setSaving(true);
    try {
      const res = await api().post(`/admin/email-settings/templates/${purpose}/reset`);
      setTemplates({ ...templates, [purpose]: { ...(templates[purpose] || {}), ...res.data } });
      toast.success(`Template '${purpose}' reset to default`);
    } catch (e) {
      toast.error('Failed to reset template');
    } finally {
      setSaving(false);
    }
  };

  const previewTemplate = async (purpose) => {
    setPreviewing({ ...previewing, [purpose]: true });
    try {
      // Save first if user has unsaved subject/body changes
      const tpl = templates[purpose] || {};
      if (tpl.subject !== undefined || tpl.body !== undefined) {
        await api().put(`/admin/email-settings/templates/${purpose}`, {
          subject: tpl.subject,
          body: tpl.body,
        });
      }
      const res = await api().post(`/admin/email-settings/templates/${purpose}/preview`, {});
      setPreviewResult({ ...previewResult, [purpose]: res.data });
    } catch (e) {
      toast.error('Failed to render preview');
    } finally {
      setPreviewing({ ...previewing, [purpose]: false });
    }
  };

  const sendTemplateTest = async (purpose) => {
    if (!testRecipient || !testRecipient.includes('@')) {
      toast.error('Enter a recipient email at the top of the Email tab first');
      return;
    }
    try {
      const res = await api().post(`/admin/email-settings/templates/${purpose}/test`, { to: testRecipient });
      const status = res.data?.status;
      if (status === 'sent') toast.success(`Templated test sent · id=${(res.data.id || '').slice(0, 8)}…`);
      else if (status === 'skipped') toast.error(`Skipped: ${res.data?.reason || 'unknown'}`);
      else toast.error(`Resend error: ${res.data?.error || 'unknown'}`);
      loadEmailHistory();
    } catch (e) {
      toast.error('Failed to send templated test');
    }
  };

  const addRecipient = (purposeKey) => {
    const draft = (recipientInputs[purposeKey] || '').trim();
    if (!draft || !draft.includes('@')) {
      toast.error('Enter a valid email address');
      return;
    }
    const current = emailConfig.recipient_lists[purposeKey] || [];
    if (current.includes(draft)) {
      toast.error('Already in list');
      return;
    }
    setEmailConfig({
      ...emailConfig,
      recipient_lists: { ...emailConfig.recipient_lists, [purposeKey]: [...current, draft] },
    });
    setRecipientInputs({ ...recipientInputs, [purposeKey]: '' });
  };

  const removeRecipient = (purposeKey, addr) => {
    const current = emailConfig.recipient_lists[purposeKey] || [];
    setEmailConfig({
      ...emailConfig,
      recipient_lists: { ...emailConfig.recipient_lists, [purposeKey]: current.filter((x) => x !== addr) },
    });
  };

  const togglePurpose = (purposeKey, value) => {
    setEmailConfig({
      ...emailConfig,
      enabled_purposes: { ...emailConfig.enabled_purposes, [purposeKey]: value },
    });
  };

  const saveNotificationSettings = async () => {
    setSaving(true);
    try {
      await api().put('/admin/notification-settings', notificationSettings);
      toast.success('Notification settings saved');
    } catch (error) {
      toast.error('Failed to save notification settings');
    } finally {
      setSaving(false);
    }
  };

  const loadTemplate = (templateId) => {
    setSelectedTemplate(templateId);
    const template = emailTemplates[templateId];
    if (template) {
      setTemplateSubject(template.subject);
      setTemplateBody(template.body);
    }
  };

  // ===== iter31 handlers: Timecodes =====
  const openTimecodeAdd = () => {
    setTimecodeEditing(null);
    setTimecodeForm({ label: '', color: 'bg-slate-500', counts_toward_hours: true, is_payable: true, requires_work_order: false, active: true, sort_order: 100 });
    setTimecodeDialogOpen(true);
  };
  const openTimecodeEdit = (tc) => {
    setTimecodeEditing(tc);
    setTimecodeForm({
      label: tc.label || '', color: tc.color || 'bg-slate-500',
      counts_toward_hours: tc.counts_toward_hours !== false, is_payable: tc.is_payable !== false,
      requires_work_order: !!tc.requires_work_order, active: tc.active !== false, sort_order: tc.sort_order || 100,
    });
    setTimecodeDialogOpen(true);
  };
  const saveTimecode = async () => {
    if (!timecodeForm.label.trim()) { toast.error('Label is required'); return; }
    try {
      if (timecodeEditing) {
        await api().put(`/timecode-options/${timecodeEditing.id}`, timecodeForm);
        toast.success('Timecode updated');
      } else {
        await api().post('/timecode-options', timecodeForm);
        toast.success('Timecode created');
      }
      setTimecodeDialogOpen(false);
      const res = await api().get('/timecode-options');
      setTimecodes(res.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deleteTimecode = async (tc) => {
    if (!window.confirm(`Delete timecode "${tc.label}"? If it's in use, it will be deactivated instead.`)) return;
    try {
      await api().delete(`/timecode-options/${tc.id}`);
      toast.success('Done');
      const res = await api().get('/timecode-options');
      setTimecodes(res.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // ===== iter31 handlers: Users =====
  const openUserAdd = () => {
    setUserEditing(null);
    setUserForm({ email: '', full_name: '', role: 'Operator', password: '', is_active: true, birthday: '', hire_date: '' });
    setUserDialogOpen(true);
  };
  const openUserEdit = (u) => {
    setUserEditing(u);
    setUserForm({
      email: u.email,
      full_name: u.full_name || '',
      role: u.role || 'Operator',
      password: '',
      is_active: u.is_active !== false,
      birthday: (u.birthday || '').slice(0, 10),
      hire_date: (u.hire_date || '').slice(0, 10),
    });
    setUserDialogOpen(true);
  };

  // iter52: pending approvals state + handlers
  const [pendingUsers, setPendingUsers] = useState([]);
  const [approveBusy, setApproveBusy] = useState({});
  const [rejectingUser, setRejectingUser] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  // iter53: auto-approve auth config
  const [authConfig, setAuthConfig] = useState({
    auto_approve_enabled: false,
    auto_approve_domains: [],
    auto_approve_default_role: 'Operator',
  });
  const [domainInput, setDomainInput] = useState('');

  // iter54: branding config
  const [brandingForm, setBrandingForm] = useState(null);

  const setBrandField = (k, v) => setBrandingForm(prev => ({ ...(prev || {}), [k]: v }));

  // iter55: TV Display — Company News + Safety
  const [newsItems, setNewsItems] = useState([]);
  const [newsDraft, setNewsDraft] = useState({ id: null, title: '', body: '', image_data: '', starts_at: '', ends_at: '', pinned: false, show_on_tv: true });
  const [newsDialogOpen, setNewsDialogOpen] = useState(false);
  const [safetyForm, setSafetyForm] = useState({ last_incident_date: '', record_days: 0, incident_note: '', reset_record_to_current: false });

  // iter56: Weather settings
  const [weatherForm, setWeatherForm] = useState({ enabled: true, latitude: 51.0447, longitude: -114.0719, location_label: 'Calgary, AB', units: 'fahrenheit', wind_units: 'mph', show_on_company_news: true, show_alerts: true, cache_minutes: 15 });
  const [weatherPreview, setWeatherPreview] = useState(null);
  const [weatherTesting, setWeatherTesting] = useState(false);

  // iter60: Backups
  const [backupsData, setBackupsData] = useState(null);
  const [backupRunning, setBackupRunning] = useState(false);
  const [backupConfig, setBackupConfig] = useState({ enabled: true, schedule_hour_utc: 7, keep_daily: 7, keep_weekly: 4, keep_monthly: 6, max_total_mb: 5000 });

  const loadBackups = async () => {
    try {
      const r = await api().get('/admin/backups');
      setBackupsData(r.data);
      if (r.data?.config) {
        setBackupConfig({
          enabled: r.data.config.enabled !== false,
          schedule_hour_utc: r.data.config.schedule_hour_utc ?? 7,
          keep_daily: r.data.config.keep_daily ?? 7,
          keep_weekly: r.data.config.keep_weekly ?? 4,
          keep_monthly: r.data.config.keep_monthly ?? 6,
          max_total_mb: r.data.config.max_total_mb ?? 5000,
        });
      }
    } catch {}
  };

  const runBackupNow = async () => {
    setBackupRunning(true);
    try {
      const r = await api().post('/admin/backups/run');
      if (r.data?.ok) toast.success(`Backup created (${(r.data.size_bytes / 1024 / 1024).toFixed(1)} MB)`);
      else toast.error(r.data?.error || 'Backup failed');
      await loadBackups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backup failed');
    } finally {
      setBackupRunning(false);
    }
  };

  const downloadBackup = (filename) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/backups/file/${encodeURIComponent(filename)}`;
    api().get(`/admin/backups/file/${encodeURIComponent(filename)}`, { responseType: 'blob' })
      .then(res => {
        const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = blobUrl; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast.error('Download failed'));
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Permanently delete ${filename}?`)) return;
    try {
      await api().delete(`/admin/backups/file/${encodeURIComponent(filename)}`);
      toast.success('Deleted');
      await loadBackups();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const saveBackupConfig = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: !!backupConfig.enabled,
        schedule_hour_utc: Number(backupConfig.schedule_hour_utc),
        keep_daily: Number(backupConfig.keep_daily),
        keep_weekly: Number(backupConfig.keep_weekly),
        keep_monthly: Number(backupConfig.keep_monthly),
        max_total_mb: Number(backupConfig.max_total_mb),
      };
      await api().put('/admin/backups/config', payload);
      toast.success('Backup settings saved');
      await loadBackups();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // iter59: TV Stations (per-TV named configs)
  const [stations, setStations] = useState([]);
  const [stationDialogOpen, setStationDialogOpen] = useState(false);
  const [stationDraft, setStationDraft] = useState({
    id: null, name: '', description: '',
    viewpoints: ['production', 'quality_pulse', 'finished_today'],
    rotation_seconds: 30,
    default_period: 'weekly',
    product_type_id: '',
    audio_alerts: true,
    visual_alerts: true,
    is_active: true,
  });
  const [stationProductTypes, setStationProductTypes] = useState([]);

  const TV_VIEWPOINT_OPTIONS = [
    { key: 'production', label: 'Production Floor' },
    { key: 'open_orders', label: 'Open Orders' },
    { key: 'inventory_pulse', label: 'Inventory Pulse' },
    { key: 'on_shift', label: 'On Shift Now' },
    { key: 'quality_pulse', label: 'Quality Pulse' },
    { key: 'finished_today', label: 'Finished Today' },
    { key: 'repair_board', label: 'Repair Board' },
    { key: 'delivery_schedule', label: 'Delivery Schedule' },
    { key: 'active_recalls', label: 'Active Recalls' },
    { key: 'safety', label: 'Safety' },
    { key: 'celebrations', label: 'Celebrations' },
    { key: 'company_news', label: 'Company News' },
  ];

  const openStationAdd = () => {
    setStationDraft({
      id: null, name: '', description: '',
      viewpoints: ['production', 'quality_pulse', 'finished_today'],
      rotation_seconds: 30, default_period: 'weekly',
      product_type_id: '', audio_alerts: true, visual_alerts: true, is_active: true,
    });
    setStationDialogOpen(true);
  };
  const openStationEdit = (s) => {
    setStationDraft({
      id: s.id,
      name: s.name || '',
      description: s.description || '',
      viewpoints: s.viewpoints || [],
      rotation_seconds: s.rotation_seconds || 30,
      default_period: s.default_period || 'weekly',
      product_type_id: s.product_type_id || '',
      audio_alerts: s.audio_alerts !== false,
      visual_alerts: s.visual_alerts !== false,
      is_active: s.is_active !== false,
    });
    setStationDialogOpen(true);
  };
  const toggleViewpoint = (key) => {
    setStationDraft(d => {
      const has = d.viewpoints.includes(key);
      return { ...d, viewpoints: has ? d.viewpoints.filter(v => v !== key) : [...d.viewpoints, key] };
    });
  };
  const moveViewpoint = (key, dir) => {
    setStationDraft(d => {
      const list = [...d.viewpoints];
      const i = list.indexOf(key);
      if (i < 0) return d;
      const j = i + dir;
      if (j < 0 || j >= list.length) return d;
      [list[i], list[j]] = [list[j], list[i]];
      return { ...d, viewpoints: list };
    });
  };
  const saveStation = async () => {
    if (!stationDraft.name.trim()) { toast.error('Station name is required'); return; }
    if (!stationDraft.viewpoints.length) { toast.error('Select at least one viewpoint'); return; }
    setSaving(true);
    try {
      const payload = {
        name: stationDraft.name,
        description: stationDraft.description || '',
        viewpoints: stationDraft.viewpoints,
        rotation_seconds: Number(stationDraft.rotation_seconds || 30),
        default_period: stationDraft.default_period,
        product_type_id: stationDraft.product_type_id || null,
        audio_alerts: !!stationDraft.audio_alerts,
        visual_alerts: !!stationDraft.visual_alerts,
        is_active: !!stationDraft.is_active,
      };
      if (stationDraft.id) {
        await api().put(`/admin/tv-stations/${stationDraft.id}`, payload);
      } else {
        await api().post('/admin/tv-stations', payload);
      }
      toast.success('Station saved');
      setStationDialogOpen(false);
      const r = await api().get('/admin/tv-stations');
      setStations(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save station');
    } finally {
      setSaving(false);
    }
  };
  const deleteStation = async (id) => {
    if (!window.confirm('Delete this TV station? Any TVs pointing at it will fall back to local config.')) return;
    try {
      await api().delete(`/admin/tv-stations/${id}`);
      toast.success('Station deleted');
      const r = await api().get('/admin/tv-stations');
      setStations(r.data || []);
    } catch (e) {
      toast.error('Failed to delete');
    }
  };
  const copyStationUrl = (s) => {
    const url = `${window.location.origin}/tv?station=${s.id}`;
    try {
      navigator.clipboard.writeText(url);
      toast.success(`URL copied — open this on TV at ${s.name}`);
    } catch {
      toast.info(url);
    }
  };

  const loadTvDisplay = async () => {
    try {
      const [n, s, w, st, pt] = await Promise.all([
        api().get('/admin/company-news').catch(() => ({ data: [] })),
        api().get('/admin/safety-settings').catch(() => ({ data: {} })),
        api().get('/admin/weather-settings').catch(() => ({ data: null })),
        api().get('/admin/tv-stations').catch(() => ({ data: [] })),
        api().get('/product-types').catch(() => ({ data: [] })),
      ]);
      setNewsItems(n.data || []);
      setStations(st.data || []);
      setStationProductTypes(pt.data || []);
      setSafetyForm({
        last_incident_date: s.data?.last_incident_date || '',
        record_days: s.data?.record_days || 0,
        incident_note: s.data?.incident_note || '',
        reset_record_to_current: false,
      });
      if (w.data) {
        setWeatherForm({
          enabled: w.data.enabled !== false,
          latitude: w.data.latitude ?? 51.0447,
          longitude: w.data.longitude ?? -114.0719,
          location_label: w.data.location_label || 'Calgary, AB',
          units: w.data.units || 'fahrenheit',
          wind_units: w.data.wind_units || 'mph',
          show_on_company_news: w.data.show_on_company_news !== false,
          show_alerts: w.data.show_alerts !== false,
          cache_minutes: w.data.cache_minutes ?? 15,
        });
      }
    } catch {}
  };

  const saveWeather = async () => {
    const lat = Number(weatherForm.latitude);
    const lon = Number(weatherForm.longitude);
    if (!Number.isFinite(lat) || lat < -90 || lat > 90) {
      toast.error('Latitude must be between -90 and 90');
      return;
    }
    if (!Number.isFinite(lon) || lon < -180 || lon > 180) {
      toast.error('Longitude must be between -180 and 180');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...weatherForm,
        latitude: lat,
        longitude: lon,
        cache_minutes: Number(weatherForm.cache_minutes || 15),
      };
      await api().put('/admin/weather-settings', payload);
      toast.success('Weather settings saved');
      // Auto-preview
      try {
        const r = await api().post('/admin/weather-settings/preview');
        setWeatherPreview(r.data);
      } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save weather');
    } finally {
      setSaving(false);
    }
  };

  const previewWeather = async () => {
    setWeatherTesting(true);
    try {
      const r = await api().post('/admin/weather-settings/preview');
      setWeatherPreview(r.data);
      toast.success(`Loaded weather for ${r.data?.location_label || 'location'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Weather provider error');
    } finally {
      setWeatherTesting(false);
    }
  };

  const openNewsDialog = (item = null) => {
    if (item) {
      setNewsDraft({
        id: item.id,
        title: item.title || '',
        body: item.body || '',
        image_data: item.image_data || '',
        starts_at: (item.starts_at || '').slice(0, 10),
        ends_at: (item.ends_at || '').slice(0, 10),
        pinned: !!item.pinned,
        show_on_tv: item.show_on_tv !== false,
      });
    } else {
      setNewsDraft({ id: null, title: '', body: '', image_data: '', starts_at: '', ends_at: '', pinned: false, show_on_tv: true });
    }
    setNewsDialogOpen(true);
  };

  const onNewsImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const data = await fileToDataUri(f);
      setNewsDraft(d => ({ ...d, image_data: data }));
    } catch (err) {
      toast.error(err.message || 'Failed to read image');
    }
    if (e.target) e.target.value = '';
  };

  const saveNews = async () => {
    if (!newsDraft.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: newsDraft.title,
        body: newsDraft.body,
        image_data: newsDraft.image_data || '',
        starts_at: newsDraft.starts_at || null,
        ends_at: newsDraft.ends_at || null,
        pinned: !!newsDraft.pinned,
        show_on_tv: !!newsDraft.show_on_tv,
      };
      if (newsDraft.id) {
        await api().put(`/admin/company-news/${newsDraft.id}`, payload);
      } else {
        await api().post('/admin/company-news', payload);
      }
      toast.success('News item saved');
      setNewsDialogOpen(false);
      await loadTvDisplay();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const deleteNews = async (id) => {
    if (!window.confirm('Delete this news item?')) return;
    try {
      await api().delete(`/admin/company-news/${id}`);
      toast.success('Deleted');
      await loadTvDisplay();
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const saveSafety = async () => {
    setSaving(true);
    try {
      const payload = {
        last_incident_date: safetyForm.last_incident_date || null,
        record_days: Number(safetyForm.record_days || 0),
        incident_note: safetyForm.incident_note || '',
        reset_record_to_current: !!safetyForm.reset_record_to_current,
      };
      const res = await api().put('/admin/safety-settings', payload);
      setSafetyForm(s => ({ ...s, record_days: res.data?.record_days || s.record_days, reset_record_to_current: false }));
      toast.success('Safety settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const fileToDataUri = (file) => new Promise((resolve, reject) => {
    if (!file) return resolve('');
    if (file.size > 6 * 1024 * 1024) return reject(new Error('File too large — keep under 6 MB'));
    const r = new FileReader();
    r.onload = () => resolve(r.result || '');
    r.onerror = () => reject(new Error('Read failed'));
    r.readAsDataURL(file);
  });

  const onBrandImageChange = async (field, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const data = await fileToDataUri(f);
      setBrandField(field, data);
    } catch (err) {
      toast.error(err.message || 'Failed to read image');
    }
    // Reset input so re-uploading the same file re-triggers
    if (e.target) e.target.value = '';
  };

  const saveBranding = async () => {
    if (!brandingForm) return;
    setSaving(true);
    try {
      const res = await api().put('/admin/branding-settings', brandingForm);
      setBrandingForm(res.data);
      toast.success('Branding saved');
      // iter54: refresh BrandingContext so Layout/Login/TV pick up new logo + colors
      try { await brandingCtx.refresh(); } catch {}
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  const resetBranding = async () => {
    setSaving(true);
    try {
      const res = await api().post('/admin/branding-settings/reset');
      setBrandingForm(res.data);
      toast.success('Branding reset to defaults');
      try { await brandingCtx.refresh(); } catch {}
    } catch (e) {
      toast.error('Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  const addAutoDomain = () => {
    let s = domainInput.trim().toLowerCase().replace(/^@/, '');
    if (!s) return;
    if (!s.includes('.')) { toast.error("Use a real domain like 'company.com'"); return; }
    if (authConfig.auto_approve_domains.includes(s)) { toast.error('Already in list'); return; }
    setAuthConfig({ ...authConfig, auto_approve_domains: [...authConfig.auto_approve_domains, s] });
    setDomainInput('');
  };

  const removeAutoDomain = (d) => {
    setAuthConfig({ ...authConfig, auto_approve_domains: authConfig.auto_approve_domains.filter(x => x !== d) });
  };

  const saveAuthConfig = async () => {
    setSaving(true);
    try {
      const res = await api().put('/admin/auth-settings', {
        auto_approve_enabled: authConfig.auto_approve_enabled,
        auto_approve_domains: authConfig.auto_approve_domains,
        auto_approve_default_role: authConfig.auto_approve_default_role,
      });
      setAuthConfig(res.data);
      toast.success('Auto-approve settings saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const refreshUsers = async () => {
    try {
      const [allRes, pendRes] = await Promise.all([
        api().get('/users'),
        api().get('/users/pending').catch(() => ({ data: [] })),
      ]);
      setUsers(allRes.data || []);
      setPendingUsers(pendRes.data || []);
    } catch (e) {
      // silent
    }
  };

  const approvePending = async (u, roleOverride = null) => {
    setApproveBusy({ ...approveBusy, [u.id]: true });
    try {
      const body = roleOverride ? { role: roleOverride } : {};
      await api().post(`/users/${u.id}/approve`, body);
      toast.success(`Approved ${u.email}`);
      await refreshUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Approve failed');
    } finally {
      setApproveBusy({ ...approveBusy, [u.id]: false });
    }
  };

  const submitReject = async () => {
    if (!rejectingUser) return;
    setApproveBusy({ ...approveBusy, [rejectingUser.id]: true });
    try {
      await api().post(`/users/${rejectingUser.id}/reject`, { reason: rejectReason });
      toast.success(`Rejected ${rejectingUser.email}`);
      setRejectingUser(null); setRejectReason('');
      await refreshUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reject failed');
    } finally {
      setApproveBusy({ ...approveBusy, [rejectingUser?.id]: false });
    }
  };
  const saveUser = async () => {
    if (!userForm.email.trim() || !userForm.full_name.trim()) { toast.error('Email and Name are required'); return; }
    try {
      if (userEditing) {
        await api().put(`/users/${userEditing.id}`, {
          full_name: userForm.full_name,
          role: userForm.role,
          is_active: userForm.is_active,
          birthday: userForm.birthday || null,
          hire_date: userForm.hire_date || null,
        });
        toast.success('User updated');
      } else {
        if (!userForm.password || userForm.password.length < 6) { toast.error('Password ≥ 6 chars required'); return; }
        await api().post('/users', userForm);
        toast.success('User created');
      }
      setUserDialogOpen(false);
      const res = await api().get('/users');
      setUsers(res.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deleteUser = async (u) => {
    if (!window.confirm(`Delete user "${u.full_name || u.email}"? This cannot be undone.`)) return;
    try {
      await api().delete(`/users/${u.id}`);
      toast.success('User deleted');
      const res = await api().get('/users');
      setUsers(res.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  // iter60: reset a user's failed-login counter
  const resetFailedLogins = async (u) => {
    try {
      await api().post(`/users/${u.id}/reset-failed-logins`);
      toast.success(`Reset failed-login counter for ${u.email}`);
      const r = await api().get('/users');
      setUsers(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset');
    }
  };

  // --- iter34: Board Pack branding handlers ---
  const saveBoardPack = async () => {
    setSaving(true);
    try {
      await api().put('/admin/board-pack-settings', boardPack);
      toast.success('Board Pack settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };
  const onLogoUpload = async (file) => {
    if (!file) return;
    if (file.size > 400 * 1024) { toast.error('Logo file must be < 400KB'); return; }
    const reader = new FileReader();
    reader.onload = () => setBoardPack({ ...boardPack, logo_data: reader.result });
    reader.readAsDataURL(file);
  };

  // ===== iter31 handlers: Roles =====
  const openRoleAdd = () => {
    setRoleEditing(null);
    const emptyPerms = {};
    (permModules.modules || []).forEach(m => { emptyPerms[m.key] = []; });
    setRoleForm({ name: '', description: '', permissions: emptyPerms, active: true });
    setRoleDialogOpen(true);
  };
  const openRoleEdit = (r) => {
    setRoleEditing(r);
    const perms = {};
    (permModules.modules || []).forEach(m => { perms[m.key] = (r.permissions && r.permissions[m.key]) || []; });
    setRoleForm({ name: r.name, description: r.description || '', permissions: perms, active: r.active !== false });
    setRoleDialogOpen(true);
  };
  const togglePerm = (moduleKey, action) => {
    setRoleForm(prev => {
      const current = new Set(prev.permissions[moduleKey] || []);
      if (current.has(action)) current.delete(action); else current.add(action);
      return { ...prev, permissions: { ...prev.permissions, [moduleKey]: Array.from(current) } };
    });
  };
  const saveRole = async () => {
    if (!roleForm.name.trim()) { toast.error('Role name is required'); return; }
    try {
      if (roleEditing) {
        const payload = { description: roleForm.description, permissions: roleForm.permissions, active: roleForm.active };
        if (!roleEditing.is_system) payload.name = roleForm.name;
        await api().put(`/roles/${roleEditing.id}`, payload);
        toast.success('Role updated');
      } else {
        await api().post('/roles', roleForm);
        toast.success('Role created');
      }
      setRoleDialogOpen(false);
      const res = await api().get('/roles');
      setRoles(res.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const deleteRole = async (r) => {
    if (r.is_system) { toast.error('System roles cannot be deleted'); return; }
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    try {
      await api().delete(`/roles/${r.id}`);
      toast.success('Role deleted');
      const res = await api().get('/roles');
      setRoles(res.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slideIn" data-testid="admin-settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Admin Settings</h1>
          <p className="text-slate-500 text-sm">System configuration and email settings</p>
        </div>
        <Button variant="outline" onClick={fetchAllSettings} className="rounded-sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="general">
            <Settings className="w-4 h-4 mr-2" />
            General
          </TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding">
            <Eye className="w-4 h-4 mr-2" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">
            <Mail className="w-4 h-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="notifications">
            <Bell className="w-4 h-4 mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="labor">
            <DollarSign className="w-4 h-4 mr-2" />
            Labor Costs
          </TabsTrigger>
          <TabsTrigger value="serial">
            <Hash className="w-4 h-4 mr-2" />
            Serial Numbers
          </TabsTrigger>
          <TabsTrigger value="purchasing" data-testid="tab-purchasing">
            <ShoppingCart className="w-4 h-4 mr-2" />
            Purchase Orders
          </TabsTrigger>
          <TabsTrigger value="currency">
            <Globe className="w-4 h-4 mr-2" />
            Currency
          </TabsTrigger>
          <TabsTrigger value="timecodes" data-testid="tab-timecodes">
            <Clock className="w-4 h-4 mr-2" />
            Timecodes
          </TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="roles" data-testid="tab-roles">
            <Shield className="w-4 h-4 mr-2" />
            Roles
          </TabsTrigger>
          <TabsTrigger value="boardpack" data-testid="tab-boardpack">
            <FileText className="w-4 h-4 mr-2" />
            Customization
          </TabsTrigger>
          <TabsTrigger value="tvdisplay" data-testid="tab-tvdisplay">
            <Tv className="w-4 h-4 mr-2" />
            TV Display
          </TabsTrigger>
          <TabsTrigger value="backups" data-testid="tab-backups">
            <Save className="w-4 h-4 mr-2" />
            Backups
          </TabsTrigger>
          <TabsTrigger value="bomtemplates" data-testid="tab-bomtemplates">
            <Link2 className="w-4 h-4 mr-2" />
            BOM Templates
          </TabsTrigger>
          <TabsTrigger value="shipping" data-testid="tab-shipping">
            <Truck className="w-4 h-4 mr-2" />
            Shipping
          </TabsTrigger>
          {user?.role === 'Admin' && (
            <TabsTrigger value="danger" data-testid="tab-danger" className="text-red-600 data-[state=active]:bg-red-50 data-[state=active]:text-red-700">
              <Trash2 className="w-4 h-4 mr-2" />
              Danger Zone
            </TabsTrigger>
          )}
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general" className="space-y-6">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg">System Settings</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Tutorial Mode</Label>
                  <p className="text-sm text-slate-500">Show tutorial for new users</p>
                </div>
                <Switch
                  checked={systemSettings.tutorial_enabled}
                  onCheckedChange={(v) => setSystemSettings({ ...systemSettings, tutorial_enabled: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Maintenance Mode</Label>
                  <p className="text-sm text-slate-500">Disable system for maintenance</p>
                </div>
                <Switch
                  checked={systemSettings.maintenance_mode}
                  onCheckedChange={(v) => setSystemSettings({ ...systemSettings, maintenance_mode: v })}
                />
              </div>
              <div className="pt-4">
                <Button onClick={saveSystemSettings} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* iter81 — Cycle-count staleness threshold */}
          <Card className="industrial-card" data-testid="cycle-count-settings-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg">Physical Inventory / Cycle Count</CardTitle>
              <p className="text-xs text-slate-500 mt-1">Controls when an item is flagged as "STALE" on the Item Master list — a reminder to include it in the next physical count.</p>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-end gap-3">
                <div className="flex-1 max-w-xs">
                  <Label className="text-xs">Stale threshold (days since last count)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="3650"
                    value={cycleCountStaleDays}
                    onChange={(e) => setCycleCountStaleDays(e.target.value)}
                    className="rounded-sm mono"
                    data-testid="cycle-count-stale-days-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Common presets: 30 (monthly), 60, 90 (quarterly — default), 180, 365 (annual).</p>
                </div>
                <Button
                  onClick={saveCycleCountSettings}
                  disabled={cycleCountSaving}
                  className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                  data-testid="cycle-count-save-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {cycleCountSaving ? 'Saving…' : 'Save Threshold'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* iter54: Branding & Customization */}
        <TabsContent value="branding" className="space-y-6" data-testid="branding-tab-content">
          {!brandingForm ? (
            <p className="text-sm text-slate-500 italic">Loading branding…</p>
          ) : (
            <>
              <Card className="industrial-card" data-testid="branding-identity-card">
                <CardHeader className="border-b border-slate-100 pb-3">
                  <CardTitle className="text-lg">Identity</CardTitle>
                  <p className="text-xs text-slate-500 mt-1">Replace the default name, tagline, and tab title throughout the app.</p>
                </CardHeader>
                <CardContent className="p-4 space-y-3">
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">App Name</Label>
                      <Input data-testid="branding-app-name" value={brandingForm.app_name || ''} onChange={(e) => setBrandField('app_name', e.target.value)} className="rounded-sm" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Browser Tab Title (blank = App Name)</Label>
                      <Input data-testid="branding-tab-title" value={brandingForm.browser_tab_title || ''} onChange={(e) => setBrandField('browser_tab_title', e.target.value)} className="rounded-sm" />
                    </div>
                    <div className="space-y-1 md:col-span-2">
                      <Label className="text-xs">Tagline (sidebar subtitle)</Label>
                      <Input data-testid="branding-tagline" value={brandingForm.tagline || ''} onChange={(e) => setBrandField('tagline', e.target.value)} className="rounded-sm" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="industrial-card" data-testid="branding-colors-card">
                <CardHeader className="border-b border-slate-100 pb-3">
                  <CardTitle className="text-lg">Color Palette</CardTitle>
                  <p className="text-xs text-slate-500 mt-1">Primary accent applied to buttons, badges, and active states.</p>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Primary Color</Label>
                      <div className="flex gap-2">
                        <Input data-testid="branding-primary-color" type="color" value={brandingForm.primary_color || '#ea580c'} onChange={(e) => setBrandField('primary_color', e.target.value)} className="rounded-sm w-16 p-1 h-9" />
                        <Input value={brandingForm.primary_color || ''} onChange={(e) => setBrandField('primary_color', e.target.value)} className="rounded-sm font-mono text-sm" placeholder="#ea580c" />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Primary Hover</Label>
                      <div className="flex gap-2">
                        <Input data-testid="branding-primary-hover" type="color" value={brandingForm.primary_color_hover || '#c2410c'} onChange={(e) => setBrandField('primary_color_hover', e.target.value)} className="rounded-sm w-16 p-1 h-9" />
                        <Input value={brandingForm.primary_color_hover || ''} onChange={(e) => setBrandField('primary_color_hover', e.target.value)} className="rounded-sm font-mono text-sm" placeholder="#c2410c" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="industrial-card" data-testid="branding-logos-card">
                <CardHeader className="border-b border-slate-100 pb-3">
                  <CardTitle className="text-lg">Logos & Imagery</CardTitle>
                  <p className="text-xs text-slate-500 mt-1">Drop in PNG, SVG, or JPG files (≤6 MB). Use a transparent PNG for the TV logo so it sits cleanly on the dark kiosk background.</p>
                </CardHeader>
                <CardContent className="p-4 space-y-4">
                  {!brandingForm.logo_data && brandingForm.fallback_logo_data && (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs text-emerald-800" data-testid="branding-logo-fallback-hint">
                      <div className="flex items-center gap-3">
                        <img src={brandingForm.fallback_logo_data} alt="Reports logo" className="h-8 w-auto object-contain" />
                        <div className="flex-1">
                          <p className="font-medium">Using your Reports logo automatically</p>
                          <p className="text-[11px] mt-0.5">Your <b>Customization → Reports logo</b> is currently powering the top bar, login page, TVs and emails. Upload a separate Primary Logo below to override it just for the app UI.</p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm"
                          onClick={() => setBrandField('logo_data', brandingForm.fallback_logo_data)}
                          data-testid="branding-logo-copy-from-reports"
                        >
                          Copy here
                        </Button>
                      </div>
                    </div>
                  )}
                  <BrandImageRow label="Primary Logo (top bar, login, reports)" field="logo_data" preview={brandingForm.logo_data || brandingForm.fallback_logo_data || ''} onChange={onBrandImageChange} onClear={() => setBrandField('logo_data', '')} testid="logo" />
                  <BrandImageRow label="TV Logo (transparent, dark bg)" field="tv_logo_data" preview={brandingForm.tv_logo_data} onChange={onBrandImageChange} onClear={() => setBrandField('tv_logo_data', '')} testid="tv-logo" dark />
                  <BrandImageRow label="TV Banner (full width)" field="banner_data" preview={brandingForm.banner_data} onChange={onBrandImageChange} onClear={() => setBrandField('banner_data', '')} testid="banner" dark />
                  <div className="space-y-1">
                    <Label className="text-xs">TV Banner Headline (overlays banner image)</Label>
                    <Input data-testid="branding-banner-text" value={brandingForm.banner_text || ''} onChange={(e) => setBrandField('banner_text', e.target.value)} placeholder="e.g. Safety first — wear PPE on the floor" className="rounded-sm" />
                  </div>
                  <BrandImageRow label="Login Hero Image" field="hero_data" preview={brandingForm.hero_data} onChange={onBrandImageChange} onClear={() => setBrandField('hero_data', '')} testid="hero" dark />
                  <div className="space-y-1">
                    <Label className="text-xs">Login Hero Tagline (multi-line)</Label>
                    <Textarea data-testid="branding-hero-tagline" rows={2} value={brandingForm.hero_tagline || ''} onChange={(e) => setBrandField('hero_tagline', e.target.value)} className="rounded-sm" />
                  </div>
                  <BrandImageRow label="Favicon (browser tab icon, ≤512×512)" field="favicon_data" preview={brandingForm.favicon_data} onChange={onBrandImageChange} onClear={() => setBrandField('favicon_data', '')} testid="favicon" />
                </CardContent>
              </Card>

              <Card className="industrial-card" data-testid="branding-visibility-card">
                <CardHeader className="border-b border-slate-100 pb-3">
                  <CardTitle className="text-lg">Where the logo appears</CardTitle>
                  <p className="text-xs text-slate-500 mt-1">Toggle off any spot where you'd rather show the App Name as text.</p>
                </CardHeader>
                <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    ['show_logo_in_top_bar', 'Show logo in top bar / sidebar'],
                    ['show_logo_on_tv', 'Show logo on TV / kiosk header'],
                    ['show_logo_in_emails', 'Show logo in transactional emails'],
                    ['show_logo_in_reports', 'Show logo on printed reports'],
                  ].map(([k, label]) => (
                    <div key={k} className="flex items-center justify-between border border-slate-100 rounded-sm px-3 py-2">
                      <span className="text-sm">{label}</span>
                      <Switch data-testid={`branding-${k.replace(/_/g, '-')}`} checked={brandingForm[k] !== false} onCheckedChange={(v) => setBrandField(k, v)} />
                    </div>
                  ))}
                </CardContent>
              </Card>

              <Card className="industrial-card" data-testid="branding-email-footer-card">
                <CardHeader className="border-b border-slate-100 pb-3">
                  <CardTitle className="text-lg">Email Signature / Footer</CardTitle>
                  <p className="text-xs text-slate-500 mt-1">Auto-appended to every Resend email below the body. Plain HTML allowed.</p>
                </CardHeader>
                <CardContent className="p-4">
                  <Textarea
                    data-testid="branding-email-footer"
                    rows={4}
                    value={brandingForm.email_footer_html || ''}
                    onChange={(e) => setBrandField('email_footer_html', e.target.value)}
                    placeholder="<strong>Acme Industrial</strong> · 200 Mfg Way · 555-0100<br/>This message is confidential."
                    className="rounded-sm font-mono text-xs"
                  />
                </CardContent>
              </Card>

              {/* iter58: Brand Preview — see exactly how branding renders across the app */}
              <BrandPreviewCard
                form={brandingForm}
                fallbackLogo={brandingForm.fallback_logo_data}
                api={api}
              />

              <div className="flex items-center justify-between">
                <Button variant="outline" onClick={resetBranding} disabled={saving} className="rounded-sm text-red-600 border-red-200 hover:bg-red-50" data-testid="branding-reset-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Reset to Defaults
                </Button>
                <Button onClick={saveBranding} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="branding-save-btn">
                  <Save className="w-4 h-4 mr-2" /> Save Branding
                </Button>
              </div>
            </>
          )}
        </TabsContent>

        {/* Email Settings */}
        <TabsContent value="email" className="space-y-6" data-testid="email-tab-content">
          {/* Resend Connection Settings */}
          <Card className="industrial-card" data-testid="resend-config-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-orange-600" /> Resend Email Configuration
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Custom-configure the Resend API used for all transactional emails (low-stock alerts, recall letters, board pack, scorecard nudges, RFx outreach, and more).
              </p>
            </CardHeader>
            <CardContent className="p-4 space-y-5">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Sender Email <span className="text-red-500">*</span></Label>
                  <Input
                    data-testid="resend-sender-email-input"
                    type="email"
                    value={emailConfig.sender_email || ''}
                    onChange={(e) => setEmailConfig({ ...emailConfig, sender_email: e.target.value })}
                    placeholder="alerts@yourcompany.com"
                    className="rounded-sm"
                  />
                  <p className="text-xs text-slate-500">
                    The address Resend will send from. <strong>Must belong to a verified domain</strong> in your Resend account, otherwise sending will fail for any recipient outside the account owner's email.
                    {' '}<a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="text-orange-600 underline">Verify a domain →</a>
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Sender Name</Label>
                  <Input
                    data-testid="resend-sender-name-input"
                    value={emailConfig.sender_name || ''}
                    onChange={(e) => setEmailConfig({ ...emailConfig, sender_name: e.target.value })}
                    placeholder={branding?.app_name || 'Your Company Name'}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Reply-To Address</Label>
                  <Input
                    data-testid="resend-reply-to-input"
                    type="email"
                    value={emailConfig.reply_to || ''}
                    onChange={(e) => setEmailConfig({ ...emailConfig, reply_to: e.target.value })}
                    placeholder="(optional) replies@yourcompany.com"
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label>BCC Audit Address</Label>
                  <Input
                    data-testid="resend-bcc-audit-input"
                    type="email"
                    value={emailConfig.bcc_audit_email || ''}
                    onChange={(e) => setEmailConfig({ ...emailConfig, bcc_audit_email: e.target.value })}
                    placeholder={user?.email ? `(optional, e.g. ${user.email})` : '(optional) audit@yourcompany.com'}
                    className="rounded-sm"
                  />
                  <p className="text-xs text-slate-500">A copy of every send is BCC'd here for compliance archiving.</p>
                  {!emailConfig.bcc_audit_email && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="resend-bcc-prefill-btn"
                      onClick={() => setEmailConfig({ ...emailConfig, bcc_audit_email: user?.email || '' })}
                      className="rounded-sm h-7 text-xs"
                      disabled={!user?.email}
                    >
                      Use my email ({user?.email || '—'})
                    </Button>
                  )}
                </div>
              </div>

              <div className="border-t border-slate-100 pt-4 space-y-2">
                <Label>API Key Override</Label>
                {emailConfig.api_key_override_masked ? (
                  <div className="flex items-center gap-2">
                    <Input
                      data-testid="resend-api-key-masked"
                      value={emailConfig.api_key_override_masked}
                      readOnly
                      className="rounded-sm font-mono bg-slate-50"
                    />
                    <Button
                      data-testid="resend-clear-api-key-btn"
                      variant="outline"
                      onClick={clearApiKeyOverride}
                      disabled={saving}
                      className="rounded-sm text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Clear Override
                    </Button>
                  </div>
                ) : (
                  <Input
                    data-testid="resend-api-key-input"
                    type="password"
                    value={emailConfig.api_key_override || ''}
                    onChange={(e) => setEmailConfig({ ...emailConfig, api_key_override: e.target.value })}
                    placeholder={emailConfig.env_resend_key_present ? 'Leave blank to use env-configured key' : 'Paste your Resend API key (re_...)'}
                    className="rounded-sm font-mono"
                  />
                )}
                <p className="text-xs text-slate-500">
                  {emailConfig.env_resend_key_present
                    ? 'A Resend key is currently set in env. Anything entered here overrides it.'
                    : 'No env Resend key detected — set one here to enable email sends.'}
                  {' '}Get a key at <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-orange-600 underline">resend.com/api-keys</a>.
                </p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
                <Button
                  data-testid="resend-save-config-btn"
                  onClick={saveEmailConfig}
                  disabled={saving}
                  className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Email Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Per-purpose recipient lists */}
          <Card className="industrial-card" data-testid="resend-purposes-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg">Distribution Lists by Purpose</CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Configure which addresses get CC'd for each kind of automated email. Toggle a purpose off to silence all sends of that kind.
              </p>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {emailPurposes.length === 0 && (
                <p className="text-sm text-slate-500 italic">Loading purposes…</p>
              )}
              {emailPurposes.map((p) => {
                const recipients = emailConfig.recipient_lists?.[p.key] || [];
                const enabled = emailConfig.enabled_purposes?.[p.key] !== false;
                return (
                  <div
                    key={p.key}
                    data-testid={`purpose-row-${p.key}`}
                    className={`border ${enabled ? 'border-slate-200' : 'border-slate-200 bg-slate-50 opacity-70'} rounded-sm p-3 space-y-2`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">{p.label}</span>
                          {!enabled && <span className="text-[10px] uppercase bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded">Paused</span>}
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">{p.description}</p>
                      </div>
                      <Switch
                        data-testid={`purpose-toggle-${p.key}`}
                        checked={enabled}
                        onCheckedChange={(v) => togglePurpose(p.key, v)}
                      />
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      {recipients.length === 0 && (
                        <span className="text-xs text-slate-400 italic">No recipients configured.</span>
                      )}
                      {recipients.map((addr) => (
                        <span
                          key={addr}
                          data-testid={`recipient-chip-${p.key}-${addr}`}
                          className="inline-flex items-center gap-1 bg-orange-50 text-orange-700 text-xs px-2 py-1 rounded-sm border border-orange-100"
                        >
                          {addr}
                          <button
                            type="button"
                            onClick={() => removeRecipient(p.key, addr)}
                            data-testid={`recipient-remove-${p.key}-${addr}`}
                            className="hover:text-red-600"
                            aria-label={`Remove ${addr}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </span>
                      ))}
                    </div>

                    <div className="flex gap-2">
                      <Input
                        data-testid={`recipient-input-${p.key}`}
                        type="email"
                        placeholder="add-email@yourcompany.com"
                        value={recipientInputs[p.key] || ''}
                        onChange={(e) => setRecipientInputs({ ...recipientInputs, [p.key]: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addRecipient(p.key);
                          }
                        }}
                        className="rounded-sm h-9 text-sm"
                      />
                      <Button
                        data-testid={`recipient-add-btn-${p.key}`}
                        type="button"
                        variant="outline"
                        onClick={() => addRecipient(p.key)}
                        className="rounded-sm h-9"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" /> Add
                      </Button>
                    </div>
                  </div>
                );
              })}
              <div className="flex items-center justify-end pt-2 border-t border-slate-100">
                <Button
                  data-testid="resend-save-purposes-btn"
                  onClick={saveEmailConfig}
                  disabled={saving}
                  className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Distribution Lists
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Test send */}
          <Card className="industrial-card" data-testid="resend-test-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg">Send Test Email</CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                Verify the Resend connection end-to-end before going live.
              </p>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="flex gap-2">
                <Input
                  data-testid="resend-test-recipient-input"
                  type="email"
                  value={testRecipient}
                  onChange={(e) => setTestRecipient(e.target.value)}
                  placeholder="recipient@example.com"
                  className="rounded-sm"
                />
                <Button
                  data-testid="resend-send-test-btn"
                  onClick={sendTestEmail}
                  disabled={sendingTest || !testRecipient}
                  className="bg-orange-600 hover:bg-orange-700 rounded-sm"
                >
                  {sendingTest ? (<><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending…</>) : (<><Mail className="w-4 h-4 mr-2" /> Send Test</>)}
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                The test email is sent through the configuration above. If it fails with a "verify a domain" error, your sender email's domain hasn't been verified yet on Resend.
              </p>
            </CardContent>
          </Card>

          {/* Send history */}
          <Card className="industrial-card" data-testid="resend-history-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <div className="flex flex-row items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-lg">Send History (Compliance Archive)</CardTitle>
                  <p className="text-xs text-slate-500 mt-1">Last 50 outbound emails matching the filters below. Export for compliance review.</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    data-testid="resend-history-csv-btn"
                    variant="outline"
                    size="sm"
                    onClick={exportHistoryCsv}
                    className="rounded-sm"
                  >
                    <FileText className="w-4 h-4 mr-2" /> Export CSV
                  </Button>
                  <Button
                    data-testid="resend-history-refresh-btn"
                    variant="outline"
                    size="sm"
                    onClick={loadEmailHistory}
                    disabled={emailHistoryLoading}
                    className="rounded-sm"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${emailHistoryLoading ? 'animate-spin' : ''}`} /> Refresh
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mt-3">
                <Select
                  value={historyFilters.purpose || 'all'}
                  onValueChange={(v) => setHistoryFilters({ ...historyFilters, purpose: v === 'all' ? '' : v })}
                >
                  <SelectTrigger data-testid="history-filter-purpose" className="rounded-sm h-9 text-xs">
                    <SelectValue placeholder="All purposes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All purposes</SelectItem>
                    {emailPurposes.map((p) => (
                      <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
                    ))}
                    <SelectItem value="test">Test</SelectItem>
                  </SelectContent>
                </Select>
                <Select
                  value={historyFilters.status || 'all'}
                  onValueChange={(v) => setHistoryFilters({ ...historyFilters, status: v === 'all' ? '' : v })}
                >
                  <SelectTrigger data-testid="history-filter-status" className="rounded-sm h-9 text-xs">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="error">Error</SelectItem>
                    <SelectItem value="skipped">Skipped</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  data-testid="history-filter-start"
                  type="date"
                  value={historyFilters.start || ''}
                  onChange={(e) => setHistoryFilters({ ...historyFilters, start: e.target.value })}
                  placeholder="Start date"
                  className="rounded-sm h-9 text-xs"
                />
                <Input
                  data-testid="history-filter-end"
                  type="date"
                  value={historyFilters.end || ''}
                  onChange={(e) => setHistoryFilters({ ...historyFilters, end: e.target.value })}
                  placeholder="End date"
                  className="rounded-sm h-9 text-xs"
                />
              </div>
              <div className="flex justify-end mt-2">
                <Button
                  data-testid="history-apply-filters-btn"
                  variant="outline"
                  size="sm"
                  onClick={loadEmailHistory}
                  className="rounded-sm h-7 text-xs"
                >
                  Apply Filters
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">When</TableHead>
                    <TableHead className="text-xs">Purpose</TableHead>
                    <TableHead className="text-xs">To</TableHead>
                    <TableHead className="text-xs">Subject</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emailHistory.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-sm text-slate-400 italic py-6" data-testid="email-history-empty">
                        No sends recorded yet. Use "Send Test Email" above to verify the connection.
                      </TableCell>
                    </TableRow>
                  )}
                  {emailHistory.map((row) => (
                    <TableRow key={row.id} data-testid={`email-history-row-${row.id}`}>
                      <TableCell className="text-xs whitespace-nowrap">{row.ts ? new Date(row.ts).toLocaleString() : '—'}</TableCell>
                      <TableCell className="text-xs">{row.purpose || '—'}</TableCell>
                      <TableCell className="text-xs font-mono max-w-[180px] truncate" title={(row.to || []).join(', ')}>{(row.to || []).join(', ')}</TableCell>
                      <TableCell className="text-xs max-w-[260px] truncate" title={row.subject}>{row.subject}</TableCell>
                      <TableCell className="text-xs">
                        <span
                          className={`inline-block px-1.5 py-0.5 rounded-sm uppercase tracking-wide text-[10px] ${
                            row.status === 'sent' ? 'bg-emerald-100 text-emerald-700' :
                            row.status === 'skipped' ? 'bg-slate-100 text-slate-600' :
                            'bg-red-100 text-red-700'
                          }`}
                        >{row.status || '—'}</span>
                        {row.error && (
                          <div className="text-[10px] text-red-600 mt-0.5 line-clamp-2 max-w-[220px]" title={row.error}>{row.error}</div>
                        )}
                        {row.reason && row.status !== 'sent' && (
                          <div className="text-[10px] text-slate-500 mt-0.5">{row.reason}</div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* iter48: Per-purpose Templates editor */}
          <Card className="industrial-card" data-testid="resend-templates-card">
            <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Email Templates by Purpose</CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Customize the subject line and HTML body for each kind of automated email. Use {'{{variable_name}}'} to insert dynamic values. Click Preview to see the rendered output with sample data.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={loadTemplates}
                disabled={templatesLoading}
                className="rounded-sm"
                data-testid="templates-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${templatesLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              {emailPurposes.length === 0 && (
                <p className="text-sm text-slate-500 italic">Loading purposes…</p>
              )}
              {emailPurposes.map((p) => {
                const tpl = templates[p.key] || {};
                const variables = tpl.variables || [];
                const isCustom = tpl.is_custom;
                const preview = previewResult[p.key];
                return (
                  <details
                    key={p.key}
                    data-testid={`template-row-${p.key}`}
                    className="border border-slate-200 rounded-sm group"
                  >
                    <summary className="cursor-pointer px-3 py-2 flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition rounded-sm">
                      <div className="flex items-center gap-2">
                        <ChevronRightIcon className="w-3.5 h-3.5 text-slate-400 group-open:rotate-90 transition-transform" />
                        <span className="font-medium text-slate-900 text-sm">{p.label}</span>
                        {isCustom && <span className="text-[10px] uppercase bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">Customized</span>}
                      </div>
                      <span className="text-xs text-slate-500">{p.key}</span>
                    </summary>
                    <div className="p-3 space-y-3">
                      <p className="text-xs text-slate-500">{p.description}</p>
                      {variables.length > 0 && (
                        <div className="text-xs text-slate-600 bg-slate-50 border border-slate-100 rounded-sm p-2">
                          <span className="font-medium">Available variables: </span>
                          {variables.map((v) => (
                            <code key={v} className="inline-block mx-0.5 px-1 bg-white border border-slate-200 text-[10px] rounded">{`{{${v}}}`}</code>
                          ))}
                        </div>
                      )}
                      <div className="space-y-1">
                        <Label className="text-xs">Subject</Label>
                        <Input
                          data-testid={`template-subject-${p.key}`}
                          value={tpl.subject || ''}
                          onChange={(e) => updateTemplateField(p.key, 'subject', e.target.value)}
                          className="rounded-sm font-mono text-sm"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Body (rich text)</Label>
                        <RichTextEditor
                          testId={`template-body-${p.key}`}
                          value={tpl.body || ''}
                          onChange={(html) => updateTemplateField(p.key, 'body', html)}
                          variables={variables}
                          placeholder="Compose the email body. Use the Variable button to insert dynamic fields."
                          minHeight={220}
                        />
                      </div>
                      <div className="flex flex-wrap gap-2 pt-1">
                        <Button
                          data-testid={`template-save-${p.key}`}
                          size="sm"
                          onClick={() => saveTemplate(p.key)}
                          disabled={saving}
                          className="bg-slate-900 hover:bg-slate-800 rounded-sm h-8 text-xs"
                        >
                          <Save className="w-3.5 h-3.5 mr-1" /> Save
                        </Button>
                        <Button
                          data-testid={`template-preview-${p.key}`}
                          variant="outline"
                          size="sm"
                          onClick={() => previewTemplate(p.key)}
                          disabled={previewing[p.key]}
                          className="rounded-sm h-8 text-xs"
                        >
                          {previewing[p.key] ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Eye className="w-3.5 h-3.5 mr-1" />}
                          Preview
                        </Button>
                        <Button
                          data-testid={`template-test-send-${p.key}`}
                          variant="outline"
                          size="sm"
                          onClick={() => sendTemplateTest(p.key)}
                          disabled={!testRecipient}
                          className="rounded-sm h-8 text-xs text-orange-700 border-orange-200 hover:bg-orange-50"
                          title={!testRecipient ? 'Enter a test recipient at the top of the Email tab first' : ''}
                        >
                          <Mail className="w-3.5 h-3.5 mr-1" /> Send Test
                        </Button>
                        {isCustom && (
                          <Button
                            data-testid={`template-reset-${p.key}`}
                            variant="outline"
                            size="sm"
                            onClick={() => resetTemplate(p.key)}
                            disabled={saving}
                            className="rounded-sm h-8 text-xs text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Reset to Default
                          </Button>
                        )}
                      </div>
                      {preview && (
                        <div className="border border-slate-200 rounded-sm p-3 mt-2 bg-slate-50" data-testid={`template-preview-output-${p.key}`}>
                          <p className="text-xs text-slate-500 mb-1">Preview (rendered with sample data):</p>
                          <div className="text-sm font-medium mb-2 break-words">Subject: {preview.subject}</div>
                          <div
                            className="bg-white border border-slate-100 rounded-sm p-3 text-sm overflow-auto max-h-72"
                            // eslint-disable-next-line react/no-danger
                            dangerouslySetInnerHTML={{ __html: preview.html }}
                          />
                        </div>
                      )}
                    </div>
                  </details>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notification Settings */}
        <TabsContent value="notifications" className="space-y-6">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg">Notification Preferences</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-base">Email Notifications</Label>
                  <p className="text-sm text-slate-500">Receive notifications via email</p>
                </div>
                <Switch
                  checked={notificationSettings.email_notifications_enabled}
                  onCheckedChange={(v) => setNotificationSettings({ ...notificationSettings, email_notifications_enabled: v })}
                />
              </div>
              <div className="border-t border-slate-100 pt-4 space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Low Stock Alerts</Label>
                  <Switch
                    checked={notificationSettings.low_stock_alerts}
                    onCheckedChange={(v) => setNotificationSettings({ ...notificationSettings, low_stock_alerts: v })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Work Order Updates</Label>
                  <Switch
                    checked={notificationSettings.work_order_updates}
                    onCheckedChange={(v) => setNotificationSettings({ ...notificationSettings, work_order_updates: v })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Bug Report Updates</Label>
                  <Switch
                    checked={notificationSettings.bug_report_updates}
                    onCheckedChange={(v) => setNotificationSettings({ ...notificationSettings, bug_report_updates: v })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Daily Summary</Label>
                  <Switch
                    checked={notificationSettings.daily_summary}
                    onCheckedChange={(v) => setNotificationSettings({ ...notificationSettings, daily_summary: v })}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label>Weekly Report</Label>
                  <Switch
                    checked={notificationSettings.weekly_report}
                    onCheckedChange={(v) => setNotificationSettings({ ...notificationSettings, weekly_report: v })}
                  />
                </div>
              </div>
              <div className="pt-4">
                <Button onClick={saveNotificationSettings} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                  <Save className="w-4 h-4 mr-2" />
                  Save Notification Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Labor Cost Settings */}
        <TabsContent value="labor" className="space-y-6">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg">Default Labor Settings</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Default Hourly Rate</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={laborSettings.default_hourly_rate}
                    onChange={(e) => setLaborSettings({ ...laborSettings, default_hourly_rate: parseFloat(e.target.value) })}
                    className="rounded-sm mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Overtime Multiplier</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={laborSettings.overtime_multiplier}
                    onChange={(e) => setLaborSettings({ ...laborSettings, overtime_multiplier: parseFloat(e.target.value) })}
                    className="rounded-sm mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={laborSettings.currency} onValueChange={(v) => setLaborSettings({ ...laborSettings, currency: v })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD ($)</SelectItem>
                      <SelectItem value="EUR">EUR (€)</SelectItem>
                      <SelectItem value="GBP">GBP (£)</SelectItem>
                      <SelectItem value="CAD">CAD ($)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="pt-4">
                <Button onClick={saveLaborSettings} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm">
                  <Save className="w-4 h-4 mr-2" />
                  Save Labor Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5" />
                User Hourly Rates
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Hourly Rate</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.full_name}</TableCell>
                      <TableCell className="text-slate-500">{user.role}</TableCell>
                      <TableCell>
                        <Input
                          type="number"
                          step="0.01"
                          value={userRates[user.id] || laborSettings.default_hourly_rate}
                          onChange={(e) => setUserRates({ ...userRates, [user.id]: e.target.value })}
                          className="w-24 rounded-sm mono"
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => saveUserRate(user.id, userRates[user.id] || laborSettings.default_hourly_rate)}
                          className="rounded-sm"
                        >
                          Save
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Serial Number Settings */}
        <TabsContent value="serial" className="space-y-6">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Hash className="w-5 h-5" />
                  Serial Number Configurations
                </CardTitle>
                <Button 
                  onClick={() => setSerialDialogOpen(true)} 
                  className="bg-orange-500 hover:bg-orange-600 rounded-sm"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Config
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {serialConfigs.length > 0 ? (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Format</TableHead>
                      <TableHead>Current Seq</TableHead>
                      <TableHead>Sample</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {serialConfigs.map(config => {
                      const sampleParts = [config.prefix];
                      if (config.include_date) sampleParts.push('YYYYMMDD');
                      sampleParts.push('X'.repeat(config.padding_digits || 5));
                      if (config.suffix) sampleParts.push(config.suffix);
                      
                      return (
                        <TableRow key={config.id}>
                          <TableCell className="font-medium">{config.config_name}</TableCell>
                          <TableCell>
                            <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                              {config.prefix}-{config.include_date ? 'DATE-' : ''}{'#'.repeat(config.padding_digits || 5)}
                            </span>
                          </TableCell>
                          <TableCell className="font-mono">{config.current_sequence || 0}</TableCell>
                          <TableCell className="font-mono text-sm text-slate-500">
                            {sampleParts.join('-')}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600"
                              onClick={async () => {
                                try {
                                  await api().delete(`/serial-configs/${config.id}`);
                                  toast.success('Config deleted');
                                  fetchAllSettings();
                                } catch (error) {
                                  toast.error('Failed to delete');
                                }
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Hash className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No serial number configurations</p>
                  <Button 
                    variant="link" 
                    onClick={() => setSerialDialogOpen(true)}
                    className="text-orange-600"
                  >
                    Create your first config
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Currency Settings */}
        <TabsContent value="purchasing" className="space-y-6" data-testid="tab-content-purchasing">
          <PoTypesSettings api={api} />
          <PoAccountingSettings api={api} />
        </TabsContent>

        <TabsContent value="currency" className="space-y-6">
          <PaymentMethodsSettings api={api} />
          <CollectionsDigestSettings api={api} />
          <ReminderTemplatesSettings api={api} />
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg">Currency Configuration</CardTitle>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Default Currency</Label>
                  <Select 
                    value={currencySettings.default_currency || 'USD'} 
                    onValueChange={(v) => setCurrencySettings({ ...currencySettings, default_currency: v })}
                  >
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AVAILABLE_CURRENCIES.map(c => (
                        <SelectItem key={c.code} value={c.code}>
                          {c.symbol} {c.code} - {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">Auto-Update Rates</Label>
                    <p className="text-sm text-slate-500">Automatically fetch latest exchange rates</p>
                  </div>
                  <Switch
                    checked={currencySettings.auto_update_rates}
                    onCheckedChange={(v) => setCurrencySettings({ ...currencySettings, auto_update_rates: v })}
                  />
                </div>
              </div>

              {/* iter78h — default tax codes that pre-fill new Item Master records */}
              <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                <div className="space-y-1">
                  <Label className="text-sm">Default Sales Tax Code</Label>
                  <Input
                    value={currencySettings.default_sales_tax_code || ''}
                    onChange={(e) => setCurrencySettings({ ...currencySettings, default_sales_tax_code: e.target.value })}
                    placeholder="e.g., GST 5%, HST 13%, ZERO"
                    className="rounded-sm"
                    data-testid="currency-default-sales-tax-code"
                  />
                  <p className="text-[10px] text-slate-500">Auto-fills the Sales Tax Code on new items.</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Default Purchase Tax Code</Label>
                  <Input
                    value={currencySettings.default_purchase_tax_code || ''}
                    onChange={(e) => setCurrencySettings({ ...currencySettings, default_purchase_tax_code: e.target.value })}
                    placeholder="e.g., GST 5%, HST 13%, ZERO"
                    className="rounded-sm"
                    data-testid="currency-default-purchase-tax-code"
                  />
                  <p className="text-[10px] text-slate-500">Auto-fills the Purchase Tax Code on new items.</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Enabled Currencies</Label>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_CURRENCIES.map(c => (
                    <Button
                      key={c.code}
                      type="button"
                      variant={currencySettings.enabled_currencies?.includes(c.code) ? "default" : "outline"}
                      size="sm"
                      className="rounded-sm"
                      onClick={() => {
                        const enabled = currencySettings.enabled_currencies || [];
                        if (enabled.includes(c.code)) {
                          if (c.code !== currencySettings.default_currency) {
                            setCurrencySettings({
                              ...currencySettings,
                              enabled_currencies: enabled.filter(x => x !== c.code)
                            });
                          }
                        } else {
                          setCurrencySettings({
                            ...currencySettings,
                            enabled_currencies: [...enabled, c.code]
                          });
                        }
                      }}
                    >
                      {c.symbol} {c.code}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="pt-4 flex gap-2">
                <Button 
                  onClick={async () => {
                    setSaving(true);
                    try {
                      await api().put('/currency/settings', currencySettings);
                      toast.success('Currency settings saved');
                    } catch (error) {
                      toast.error('Failed to save settings');
                    } finally {
                      setSaving(false);
                    }
                  }} 
                  disabled={saving} 
                  className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Exchange Rates</CardTitle>
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm"
                disabled={refreshingRates}
                onClick={async () => {
                  setRefreshingRates(true);
                  try {
                    const res = await api().post('/currency/refresh-rates');
                    setCurrencyRates(res.data.rates);
                    toast.success(`Updated ${res.data.rates.length} rates`);
                  } catch (error) {
                    toast.error('Failed to refresh rates');
                  } finally {
                    setRefreshingRates(false);
                  }
                }}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${refreshingRates ? 'animate-spin' : ''}`} />
                Refresh Rates
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              {currencyRates.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>From</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Updated</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currencyRates.map((rate, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{rate.base_currency}</TableCell>
                        <TableCell>{rate.target_currency}</TableCell>
                        <TableCell>{rate.rate?.toFixed(4)}</TableCell>
                        <TableCell className="text-slate-500 text-sm">
                          {rate.updated_at ? new Date(rate.updated_at).toLocaleString() : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No exchange rates cached</p>
                  <Button 
                    variant="link" 
                    onClick={async () => {
                      setRefreshingRates(true);
                      try {
                        const res = await api().post('/currency/refresh-rates');
                        setCurrencyRates(res.data.rates);
                        toast.success(`Updated ${res.data.rates.length} rates`);
                      } catch (error) {
                        toast.error('Failed to refresh rates');
                      } finally {
                        setRefreshingRates(false);
                      }
                    }}
                    className="text-orange-600"
                  >
                    Fetch rates now
                  </Button>
                </div>
              )}
              {currencySettings.last_rate_update && (
                <p className="text-xs text-slate-400 mt-2">
                  Last updated: {new Date(currencySettings.last_rate_update).toLocaleString()}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timecodes (iter31) */}
        <TabsContent value="timecodes" className="space-y-4" data-testid="timecodes-panel">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Timecard Codes</CardTitle>
                <p className="text-xs text-slate-500">Admin-defined categories for timecard entries (sick, vacation, bereavement, other companies, etc.)</p>
              </div>
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={openTimecodeAdd} data-testid="timecode-add-btn"><Plus className="w-4 h-4 mr-1" /> Add Timecode</Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10"></TableHead>
                    <TableHead>Label</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Payable</TableHead>
                    <TableHead>Counts Hours</TableHead>
                    <TableHead>WO Required</TableHead>
                    <TableHead>Active</TableHead>
                    <TableHead>System</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {timecodes.map(tc => (
                    <TableRow key={tc.id} data-testid={`timecode-row-${tc.code}`}>
                      <TableCell><span className={`inline-block w-4 h-4 rounded ${tc.color || 'bg-slate-500'}`} /></TableCell>
                      <TableCell className="font-medium">{tc.label}</TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">{tc.code}</TableCell>
                      <TableCell>{tc.is_payable ? '✅' : '—'}</TableCell>
                      <TableCell>{tc.counts_toward_hours ? '✅' : '—'}</TableCell>
                      <TableCell>{tc.requires_work_order ? '✅' : '—'}</TableCell>
                      <TableCell>{tc.active ? <span className="text-emerald-600">Active</span> : <span className="text-slate-400">Inactive</span>}</TableCell>
                      <TableCell>{tc.is_system ? <span className="text-xs text-slate-500">system</span> : <span className="text-xs text-slate-400">custom</span>}</TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => openTimecodeEdit(tc)} data-testid={`timecode-edit-${tc.code}`}><Edit3 className="w-4 h-4" /></Button>
                        {!tc.is_system && (
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteTimecode(tc)} data-testid={`timecode-delete-${tc.code}`}><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {timecodes.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-400">No timecodes yet. Click "Add Timecode".</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Users (iter31) */}
        <TabsContent value="users" className="space-y-4" data-testid="users-panel">
          {/* iter53: Auto-Approve Domain Whitelist */}
          <Card className="industrial-card" data-testid="auto-approve-card">
            <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Auto-Approve Domain Whitelist</CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Email addresses ending in any of these domains will be auto-approved at signup with the configured default role. External signups still require manual approval.
                </p>
              </div>
              <Switch
                data-testid="auto-approve-toggle"
                checked={!!authConfig.auto_approve_enabled}
                onCheckedChange={(v) => setAuthConfig({ ...authConfig, auto_approve_enabled: v })}
              />
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="flex flex-wrap gap-1.5 min-h-[2rem]">
                {(authConfig.auto_approve_domains || []).length === 0 && (
                  <span className="text-xs text-slate-400 italic">No domains configured yet.</span>
                )}
                {(authConfig.auto_approve_domains || []).map(d => (
                  <span
                    key={d}
                    className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 text-xs px-2 py-1 rounded-sm border border-emerald-100"
                    data-testid={`auto-approve-chip-${d}`}
                  >
                    @{d}
                    <button
                      type="button"
                      onClick={() => removeAutoDomain(d)}
                      data-testid={`auto-approve-remove-${d}`}
                      className="hover:text-red-600"
                      aria-label={`Remove ${d}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div className="md:col-span-2 flex gap-2">
                  <Input
                    data-testid="auto-approve-domain-input"
                    value={domainInput}
                    onChange={(e) => setDomainInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAutoDomain(); } }}
                    placeholder="company.com"
                    className="rounded-sm h-9"
                    disabled={!authConfig.auto_approve_enabled}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={addAutoDomain}
                    disabled={!authConfig.auto_approve_enabled}
                    data-testid="auto-approve-add-btn"
                    className="rounded-sm h-9"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                  </Button>
                </div>
                <div>
                  <Select
                    value={authConfig.auto_approve_default_role || 'Operator'}
                    onValueChange={(v) => setAuthConfig({ ...authConfig, auto_approve_default_role: v })}
                    disabled={!authConfig.auto_approve_enabled}
                  >
                    <SelectTrigger data-testid="auto-approve-role-select" className="rounded-sm h-9">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Admin">Admin</SelectItem>
                      <SelectItem value="InventoryManager">Inventory Manager</SelectItem>
                      <SelectItem value="ProductionSupervisor">Production Supervisor</SelectItem>
                      <SelectItem value="Purchasing">Purchasing</SelectItem>
                      <SelectItem value="Operator">Operator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end pt-2 border-t border-slate-100">
                <Button
                  data-testid="auto-approve-save-btn"
                  onClick={saveAuthConfig}
                  disabled={saving}
                  className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                >
                  <Save className="w-4 h-4 mr-2" /> Save
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* iter52: Pending Approvals */}
          {pendingUsers.length > 0 && (
            <Card className="industrial-card border-amber-200" data-testid="pending-approvals-card">
              <CardHeader className="border-b border-amber-100 pb-3 bg-amber-50/40">
                <div className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-amber-600" />
                  <CardTitle className="text-lg">Pending Approvals</CardTitle>
                  <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">{pendingUsers.length} waiting</span>
                </div>
                <p className="text-xs text-slate-500">Review and approve new user signups. Rejected users cannot log in.</p>
              </CardHeader>
              <CardContent className="p-0">
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name / Request</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Approve as Role</TableHead>
                      <TableHead>Submitted</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingUsers.map(u => (
                      <PendingApprovalRow
                        key={u.id}
                        user={u}
                        busy={!!approveBusy[u.id]}
                        onApprove={approvePending}
                        onReject={() => setRejectingUser(u)}
                      />
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Users</CardTitle>
                <p className="text-xs text-slate-500">Full user CRUD — create, edit, reset password, deactivate.</p>
              </div>
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={openUserAdd} data-testid="user-add-btn"><UserPlus className="w-4 h-4 mr-1" /> Add User</Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Failed&nbsp;Logins</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                      <TableCell className="font-medium">{u.full_name || '—'}</TableCell>
                      <TableCell className="text-sm">{u.email}</TableCell>
                      <TableCell><span className="text-xs bg-slate-100 px-2 py-0.5 rounded-sm">{u.role}</span></TableCell>
                      <TableCell>
                        {u.approval_status === 'pending' ? <span className="text-amber-600 text-sm">Pending</span> :
                         u.approval_status === 'rejected' ? <span className="text-red-600 text-sm">Rejected</span> :
                         u.is_active !== false ? <span className="text-emerald-600 text-sm">Active</span> :
                         <span className="text-slate-400 text-sm">Disabled</span>}
                      </TableCell>
                      <TableCell className="text-right" data-testid={`user-failed-logins-${u.id}`}>
                        {(() => {
                          const n = u.failed_login_attempts || 0;
                          const tone = n === 0 ? 'text-slate-400' : n < 5 ? 'text-amber-600' : 'text-red-600 font-bold';
                          const tip = n > 0 && u.last_failed_login_at
                            ? `Last: ${new Date(u.last_failed_login_at).toLocaleString()}${u.last_failed_login_ip ? ` from ${u.last_failed_login_ip}` : ''}`
                            : 'No recent failures';
                          return <span className={`text-xs ${tone} font-mono`} title={tip}>{n}</span>;
                        })()}
                      </TableCell>
                      <TableCell className="text-right">
                        {(u.failed_login_attempts || 0) > 0 && (
                          <Button size="sm" variant="ghost" className="text-emerald-600" onClick={() => resetFailedLogins(u)} data-testid={`user-reset-failed-${u.id}`} title="Reset failed-login counter">
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => openUserEdit(u)} data-testid={`user-edit-${u.id}`} title="Edit"><Edit3 className="w-4 h-4" /></Button>
                        <UserPasswordActions u={u} isSelf={u.id === user?.id} onChanged={async () => { const x = await api().get('/users'); setUsers(x.data || []); }} />
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteUser(u)} data-testid={`user-delete-${u.id}`} title="Delete"><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Roles & Permissions Matrix (iter31) */}
        <TabsContent value="roles" className="space-y-4" data-testid="roles-panel">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Roles & Permissions</CardTitle>
                <p className="text-xs text-slate-500">Module × action matrix per role. System roles cannot be deleted; Admin retains full access.</p>
              </div>
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600" onClick={openRoleAdd} data-testid="role-add-btn"><Plus className="w-4 h-4 mr-1" /> Add Role</Button>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table className="data-table min-w-[900px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white z-10 min-w-[200px]">Role</TableHead>
                    {(permModules.modules || []).map(m => (
                      <TableHead key={m.key} className="text-xs text-center" title={m.label}>{m.label.split(' ')[0]}</TableHead>
                    ))}
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {roles.map(r => (
                    <TableRow key={r.id} data-testid={`role-row-${r.name}`}>
                      <TableCell className="sticky left-0 bg-white font-medium">
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-slate-400" />
                          <div>
                            <p>{r.name}</p>
                            {r.is_system && <span className="text-[10px] text-slate-400 uppercase">system</span>}
                            {r.description && <p className="text-xs text-slate-500">{r.description}</p>}
                          </div>
                        </div>
                      </TableCell>
                      {(permModules.modules || []).map(m => {
                        const acts = (r.permissions && r.permissions[m.key]) || [];
                        return (
                          <TableCell key={m.key} className="text-center">
                            {acts.length > 0 ? (
                              <div className="flex flex-col items-center gap-0.5">
                                {(permModules.actions || []).map(a => acts.includes(a) ? (
                                  <span key={a} className="text-[9px] bg-emerald-100 text-emerald-700 px-1 rounded" title={a}>{a[0].toUpperCase()}</span>
                                ) : null)}
                              </div>
                            ) : <span className="text-slate-300 text-xs">—</span>}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-right">
                        <Button size="sm" variant="ghost" onClick={() => openRoleEdit(r)} data-testid={`role-edit-${r.name}`}><Edit3 className="w-4 h-4" /></Button>
                        {!r.is_system && (
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteRole(r)} data-testid={`role-delete-${r.name}`}><Trash2 className="w-4 h-4" /></Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Customization (iter34/iter37) */}
        <TabsContent value="boardpack" className="space-y-4" data-testid="boardpack-panel">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg">Customization — Print Letterhead, Branding & Contact Info</CardTitle>
              <p className="text-xs text-slate-500 mt-1">Single source of truth for ALL printable reports — Board Pack, Recall Letter, Item Report, Vendor Risk Audit, Final Assembly Card, QC Print, Label Library headers.</p>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Company Name *</Label>
                  <Input value={boardPack.company_name} onChange={e => setBoardPack({ ...boardPack, company_name: e.target.value })} placeholder="e.g., Acme Manufacturing Inc." data-testid="bp-company-name" />
                </div>
                <div className="space-y-1">
                  <Label>Tagline / Business Line</Label>
                  <Input value={boardPack.company_tagline} onChange={e => setBoardPack({ ...boardPack, company_tagline: e.target.value })} placeholder="Precision assemblies since 1998" data-testid="bp-tagline" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Company Mailing Address</Label>
                  <Textarea rows={2} value={boardPack.company_address} onChange={e => setBoardPack({ ...boardPack, company_address: e.target.value })} placeholder="123 Industry Way&#10;Factoryville, CA 94000" data-testid="bp-address" />
                </div>
                <div className="space-y-1">
                  <Label>Facility / Shop Address</Label>
                  <Textarea rows={2} value={boardPack.facility_address} onChange={e => setBoardPack({ ...boardPack, facility_address: e.target.value })} placeholder="Bldg A · Dock 3&#10;Different from mailing address?" data-testid="bp-facility-address" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Contact Email</Label>
                  <Input type="email" value={boardPack.contact_email} onChange={e => setBoardPack({ ...boardPack, contact_email: e.target.value })} data-testid="bp-email" />
                </div>
                <div className="space-y-1">
                  <Label>Contact Phone</Label>
                  <Input value={boardPack.contact_phone} onChange={e => setBoardPack({ ...boardPack, contact_phone: e.target.value })} data-testid="bp-phone" />
                </div>
                <div className="space-y-1">
                  <Label>Fax</Label>
                  <Input value={boardPack.fax} onChange={e => setBoardPack({ ...boardPack, fax: e.target.value })} placeholder="optional" data-testid="bp-fax" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label>Website</Label>
                  <Input value={boardPack.website} onChange={e => setBoardPack({ ...boardPack, website: e.target.value })} placeholder="https://yourcompany.com" data-testid="bp-website" />
                </div>
                <div className="space-y-1">
                  <Label>Tax / Business #</Label>
                  <Input value={boardPack.tax_id || ''} onChange={e => setBoardPack({ ...boardPack, tax_id: e.target.value })} placeholder="e.g. GST 12345 6789 RT0001" data-testid="bp-tax-id" />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label>Secondary Contact</Label>
                  <Input value={boardPack.secondary_contact} onChange={e => setBoardPack({ ...boardPack, secondary_contact: e.target.value })} placeholder="Jane Smith · QA Manager" data-testid="bp-secondary-contact" />
                </div>
              </div>
              <div className="space-y-1">
                <Label>Header Note (top banner on Board Pack)</Label>
                <Textarea rows={2} value={boardPack.header_note} onChange={e => setBoardPack({ ...boardPack, header_note: e.target.value })} placeholder="Weekly executive summary. Distribute to: ..." data-testid="bp-header-note" />
              </div>
              <div className="space-y-1">
                <Label>Default Report Footer</Label>
                <Textarea rows={2} value={boardPack.default_report_footer} onChange={e => setBoardPack({ ...boardPack, default_report_footer: e.target.value })} placeholder="Confidential — Property of [Company]. Do not distribute without authorization." data-testid="bp-footer" />
              </div>
              <div className="space-y-1">
                <Label>Logo (PNG/JPG, max 400KB) — appears on every printable report</Label>
                <div className="flex items-center gap-4">
                  {boardPack.logo_data && <img src={boardPack.logo_data} alt="logo" className="h-12 object-contain border border-slate-200 rounded-sm bg-white px-2 py-1" />}
                  <Input type="file" accept="image/*" onChange={e => onLogoUpload(e.target.files?.[0])} className="max-w-sm" data-testid="bp-logo-upload" />
                  {boardPack.logo_data && (
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setBoardPack({ ...boardPack, logo_data: '' })} data-testid="bp-logo-clear">Remove</Button>
                  )}
                </div>
              </div>
              <div className="pt-2">
                <Button onClick={saveBoardPack} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="bp-save-btn">
                  <Save className="w-4 h-4 mr-2" /> Save Branding
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* iter55: TV Display — Company News + Safety counter + Weather (iter56) */}
        <TabsContent value="tvdisplay" className="space-y-6" data-testid="tvdisplay-panel">
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
            <p className="text-sm text-amber-800 font-medium">TV Display Control Center</p>
            <p className="text-xs text-amber-700 mt-1">Everything that shows on shop-floor TVs is managed here: announcements, safety counter, local weather, and (via the Branding tab) the logo + colors. Changes take effect within 60 seconds on every connected TV.</p>
          </div>

          <Card className="industrial-card" data-testid="tv-stations-card">
            <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2"><Tv className="w-5 h-5" /> TV Stations</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Pre-configured rotations for each TV. Open a station's URL on its TV and it auto-cycles the chosen viewpoints with the right defaults — no per-TV setup.</p>
              </div>
              <Button onClick={openStationAdd} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="station-add-btn">
                <Plus className="w-4 h-4 mr-1" /> New Station
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              {stations.length === 0 ? (
                <p className="text-sm text-slate-500 italic">No TV stations yet. Click "New Station" to define your first one (e.g., "Production Cell A").</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Viewpoints</TableHead>
                      <TableHead>Rotation</TableHead>
                      <TableHead>Alerts</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stations.map(s => (
                      <TableRow key={s.id} data-testid={`station-row-${s.id}`}>
                        <TableCell>
                          <div className="font-medium">{s.name}</div>
                          {s.description && <p className="text-[11px] text-slate-500 mt-0.5">{s.description}</p>}
                        </TableCell>
                        <TableCell className="text-xs">
                          {(s.viewpoints || []).slice(0, 4).map(v => (
                            <span key={v} className="inline-block bg-slate-100 rounded-sm px-1.5 py-0.5 mr-1 mb-1">{v}</span>
                          ))}
                          {(s.viewpoints || []).length > 4 && <span className="text-slate-500">+{s.viewpoints.length - 4}</span>}
                        </TableCell>
                        <TableCell className="text-xs font-mono">{s.rotation_seconds || 30}s</TableCell>
                        <TableCell className="text-xs">
                          {s.audio_alerts !== false && <span title="Audio" className="mr-1">🔔</span>}
                          {s.visual_alerts !== false && <span title="Flash">⚡</span>}
                          {s.audio_alerts === false && s.visual_alerts === false && <span className="text-slate-400">—</span>}
                        </TableCell>
                        <TableCell>
                          {s.is_active !== false
                            ? <span className="px-2 py-0.5 text-xs rounded-sm bg-emerald-100 text-emerald-700">Active</span>
                            : <span className="px-2 py-0.5 text-xs rounded-sm bg-slate-200 text-slate-600">Off</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => copyStationUrl(s)} title="Copy TV URL" data-testid={`station-copy-${s.id}`}>
                            <Globe className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openStationEdit(s)} data-testid={`station-edit-${s.id}`}><Edit3 className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteStation(s.id)} data-testid={`station-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card className="industrial-card" data-testid="tv-news-card">
            <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg flex items-center gap-2"><Megaphone className="w-5 h-5" /> Company News</CardTitle>
                <p className="text-xs text-slate-500 mt-1">Announcements that auto-cycle on the TV → Company News viewpoint.</p>
              </div>
              <Button onClick={() => openNewsDialog(null)} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="news-add-btn">
                <Plus className="w-4 h-4 mr-1" /> New
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              {newsItems.length === 0 ? (
                <p className="text-sm text-slate-500 italic">No announcements yet. Click "New" to post one.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-1/3">Title</TableHead>
                      <TableHead>Window</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>On TV</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {newsItems.map(n => (
                      <TableRow key={n.id} data-testid={`news-row-${n.id}`}>
                        <TableCell>
                          <div className="font-medium flex items-center gap-2">
                            {n.pinned && <Pin className="w-3 h-3 text-orange-500" />}
                            {n.title}
                          </div>
                          <p className="text-[11px] text-slate-500 mt-0.5">{(n.created_at || '').slice(0, 10)}</p>
                        </TableCell>
                        <TableCell className="text-xs">
                          {n.starts_at || n.ends_at
                            ? <>{(n.starts_at || '').slice(0, 10) || '—'} → {(n.ends_at || '').slice(0, 10) || '∞'}</>
                            : <span className="text-slate-500">Always</span>}
                        </TableCell>
                        <TableCell>
                          {n.is_active
                            ? <span className="px-2 py-0.5 text-xs rounded-sm bg-emerald-100 text-emerald-700">Active</span>
                            : <span className="px-2 py-0.5 text-xs rounded-sm bg-slate-200 text-slate-600">Inactive</span>}
                        </TableCell>
                        <TableCell>
                          {n.show_on_tv
                            ? <span className="px-2 py-0.5 text-xs rounded-sm bg-blue-100 text-blue-700">Yes</span>
                            : <span className="px-2 py-0.5 text-xs rounded-sm bg-slate-200 text-slate-600">No</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => openNewsDialog(n)} data-testid={`news-edit-${n.id}`}><Edit3 className="w-4 h-4" /></Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteNews(n.id)} data-testid={`news-delete-${n.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card className="industrial-card" data-testid="tv-safety-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg flex items-center gap-2"><HardHat className="w-5 h-5" /> Days Without a Safety Incident</CardTitle>
              <p className="text-xs text-slate-500 mt-1">Set the date of the last incident; the TV counter calculates days automatically.</p>
            </CardHeader>
            <CardContent className="p-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Last Incident Date</Label>
                  <Input
                    type="date"
                    value={safetyForm.last_incident_date || ''}
                    onChange={e => setSafetyForm(s => ({ ...s, last_incident_date: e.target.value }))}
                    data-testid="safety-incident-date"
                  />
                  <p className="text-[11px] text-slate-500">Leave blank if no incident has been recorded yet.</p>
                </div>
                <div className="space-y-1">
                  <Label>Best Record (days)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={safetyForm.record_days || 0}
                    onChange={e => setSafetyForm(s => ({ ...s, record_days: e.target.value }))}
                    data-testid="safety-record-days"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Note (optional)</Label>
                  <Input
                    value={safetyForm.incident_note || ''}
                    onChange={e => setSafetyForm(s => ({ ...s, incident_note: e.target.value }))}
                    placeholder="e.g., Reset after near-miss in Bay 2"
                    data-testid="safety-incident-note"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={!!safetyForm.reset_record_to_current}
                  onCheckedChange={v => setSafetyForm(s => ({ ...s, reset_record_to_current: v }))}
                  data-testid="safety-reset-record"
                />
                <Label className="!m-0 text-sm">Reset record to current days-since-incident on save</Label>
              </div>
              <Button onClick={saveSafety} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="safety-save-btn">
                <Save className="w-4 h-4 mr-2" /> Save Safety
              </Button>
            </CardContent>
          </Card>

          <Card className="industrial-card" data-testid="tv-weather-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg flex items-center gap-2"><Cloud className="w-5 h-5" /> Local Weather (Open-Meteo)</CardTitle>
              <p className="text-xs text-slate-500 mt-1">Free 7-day forecast from Open-Meteo · no API key required. Shows on the TV → Company News viewpoint as a side panel.</p>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  checked={!!weatherForm.enabled}
                  onCheckedChange={v => setWeatherForm(s => ({ ...s, enabled: v }))}
                  data-testid="weather-enabled"
                />
                <Label className="!m-0 text-sm">Enabled — show weather on the TV</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Location Label</Label>
                  <Input
                    value={weatherForm.location_label}
                    onChange={e => setWeatherForm(s => ({ ...s, location_label: e.target.value }))}
                    placeholder="e.g., Calgary, AB"
                    data-testid="weather-location-label"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Latitude</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    min={-90}
                    max={90}
                    value={weatherForm.latitude}
                    onChange={e => setWeatherForm(s => ({ ...s, latitude: e.target.value }))}
                    placeholder="51.0447"
                    data-testid="weather-latitude"
                  />
                </div>
                <div className="space-y-1">
                  <Label>Longitude</Label>
                  <Input
                    type="number"
                    step="0.0001"
                    min={-180}
                    max={180}
                    value={weatherForm.longitude}
                    onChange={e => setWeatherForm(s => ({ ...s, longitude: e.target.value }))}
                    placeholder="-114.0719"
                    data-testid="weather-longitude"
                  />
                </div>
              </div>
              <p className="text-[11px] text-slate-500">
                Tip: pull lat/lon from <a className="underline" href="https://www.google.com/maps" target="_blank" rel="noreferrer">Google Maps</a> — right-click your facility → click the coordinates to copy.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Temperature Units</Label>
                  <Select value={weatherForm.units} onValueChange={v => setWeatherForm(s => ({ ...s, units: v }))}>
                    <SelectTrigger data-testid="weather-units"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fahrenheit">Fahrenheit (°F)</SelectItem>
                      <SelectItem value="celsius">Celsius (°C)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Wind Units</Label>
                  <Select value={weatherForm.wind_units} onValueChange={v => setWeatherForm(s => ({ ...s, wind_units: v }))}>
                    <SelectTrigger data-testid="weather-wind-units"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mph">mph</SelectItem>
                      <SelectItem value="kmh">km/h</SelectItem>
                      <SelectItem value="ms">m/s</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Refresh Cache (minutes)</Label>
                  <Input
                    type="number"
                    min={1}
                    max={120}
                    value={weatherForm.cache_minutes}
                    onChange={e => setWeatherForm(s => ({ ...s, cache_minutes: e.target.value }))}
                    data-testid="weather-cache-minutes"
                  />
                </div>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch checked={!!weatherForm.show_on_company_news} onCheckedChange={v => setWeatherForm(s => ({ ...s, show_on_company_news: v }))} data-testid="weather-show-on-news" />
                  <Label className="!m-0 text-sm">Show on Company News viewpoint</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={!!weatherForm.show_alerts} onCheckedChange={v => setWeatherForm(s => ({ ...s, show_alerts: v }))} data-testid="weather-show-alerts" />
                  <Label className="!m-0 text-sm">Show severe-weather alerts</Label>
                </div>
              </div>
              <div className="flex items-center gap-2 pt-1">
                <Button onClick={saveWeather} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="weather-save-btn">
                  <Save className="w-4 h-4 mr-2" /> Save
                </Button>
                <Button variant="outline" onClick={previewWeather} disabled={weatherTesting} className="rounded-sm" data-testid="weather-preview-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${weatherTesting ? 'animate-spin' : ''}`} /> Test live forecast
                </Button>
              </div>
              {weatherPreview && weatherPreview.current && (
                <div className="border border-slate-200 rounded-sm p-4 bg-slate-50" data-testid="weather-preview-block">
                  <p className="text-xs uppercase tracking-widest text-slate-500 mb-2">{weatherPreview.location_label}</p>
                  <div className="flex items-center gap-4">
                    <div className="text-4xl font-mono font-bold">{Math.round(weatherPreview.current.temperature)}°{weatherPreview.units === 'celsius' ? 'C' : 'F'}</div>
                    <div className="text-sm">
                      <div className="font-medium">{weatherPreview.current.label}</div>
                      <div className="text-slate-500">Wind {Math.round(weatherPreview.current.wind_speed || 0)} {weatherPreview.wind_units}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-7 gap-2 mt-3">
                    {(weatherPreview.daily || []).map((d, i) => (
                      <div key={d.date} className="text-center text-xs border border-slate-200 rounded-sm p-1.5 bg-white">
                        <div className="text-slate-500">{i === 0 ? 'Today' : new Date(d.date + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'short' })}</div>
                        <div className="text-slate-400 truncate">{d.label.split(' ')[0]}</div>
                        <div className="font-mono mt-1"><span className="text-orange-600 font-bold">{Math.round(d.high)}°</span> / <span className="text-slate-500">{Math.round(d.low)}°</span></div>
                        {d.precip_prob > 30 && <div className="text-blue-600 font-mono">{d.precip_prob}%</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg flex items-center gap-2"><Users className="w-5 h-5" /> User Birthdays & Hire Dates</CardTitle>
              <p className="text-xs text-slate-500 mt-1">Powers the TV → Celebrations viewpoint. Edit per user from the Users tab.</p>
            </CardHeader>
            <CardContent className="p-4">
              <p className="text-sm text-slate-600">Open the <b>Users</b> tab and click the pencil on any user to add their birthday (MM-DD) and hire date.</p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* iter60: Database Backups */}
        <TabsContent value="backups" className="space-y-6" data-testid="backups-panel">
          <div className="bg-blue-50 border border-blue-200 rounded-sm p-3">
            <p className="text-sm text-blue-800 font-medium">Database Backups</p>
            <p className="text-xs text-blue-700 mt-1">A daily mongodump runs automatically and is gzipped to <code>{backupsData?.directory || '/data/backups'}</code>. Retention pruning runs on every backup. Restoring requires manual <code>mongorestore</code> on a recovery machine — there is no one-click restore by design.</p>
          </div>

          <Card className="industrial-card" data-testid="backups-list-card">
            <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Stored Backups</CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  {backupsData ? (
                    <>
                      <b>{backupsData.files?.length || 0}</b> backup(s) · total{' '}
                      <b>{((backupsData.total_bytes || 0) / 1024 / 1024).toFixed(1)} MB</b>
                      {!backupsData.mongodump_available && <span className="text-red-600 ml-2">⚠ mongodump not installed on host</span>}
                    </>
                  ) : 'Loading…'}
                </p>
              </div>
              <Button onClick={runBackupNow} disabled={backupRunning} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="backup-run-now">
                <Save className={`w-4 h-4 mr-2 ${backupRunning ? 'animate-pulse' : ''}`} /> {backupRunning ? 'Backing up…' : 'Run Backup Now'}
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              {!backupsData ? <p className="text-sm text-slate-500 italic">Loading…</p> : backupsData.files?.length === 0 ? (
                <p className="text-sm text-slate-500 italic">No backups yet. Click "Run Backup Now" to create your first one.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Filename</TableHead>
                      <TableHead className="text-right">Size</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {backupsData.files.map(f => (
                      <TableRow key={f.filename} data-testid={`backup-row-${f.filename}`}>
                        <TableCell className="font-mono text-xs">{f.filename}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{(f.size_bytes / 1024 / 1024).toFixed(2)} MB</TableCell>
                        <TableCell className="text-xs">{new Date(f.created_at).toLocaleString()}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => downloadBackup(f.filename)} data-testid={`backup-download-${f.filename}`} title="Download">
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteBackup(f.filename)} data-testid={`backup-delete-${f.filename}`} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card className="industrial-card" data-testid="backups-config-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <CardTitle className="text-lg">Schedule & Retention</CardTitle>
              <p className="text-xs text-slate-500 mt-1">All times in UTC. Retention runs after every backup.</p>
            </CardHeader>
            <CardContent className="p-4 space-y-4">
              <div className="flex items-center gap-3">
                <Switch checked={!!backupConfig.enabled} onCheckedChange={v => setBackupConfig(s => ({ ...s, enabled: v }))} data-testid="backup-enabled" />
                <Label className="!m-0 text-sm">Daily backup enabled</Label>
              </div>
              {/* iter71 — retention preset dropdown */}
              <div className="space-y-1">
                <Label>Retention Preset</Label>
                <Select onValueChange={(v) => {
                  const presets = {
                    minimal: { keep_daily: 3, keep_weekly: 0, keep_monthly: 0, max_total_mb: 1000 },
                    standard: { keep_daily: 7, keep_weekly: 4, keep_monthly: 3, max_total_mb: 3000 },
                    extended: { keep_daily: 14, keep_weekly: 8, keep_monthly: 6, max_total_mb: 8000 },
                    compliance: { keep_daily: 30, keep_weekly: 12, keep_monthly: 24, max_total_mb: 20000 },
                  };
                  if (presets[v]) setBackupConfig(s => ({ ...s, ...presets[v] }));
                }}>
                  <SelectTrigger className="rounded-sm max-w-md" data-testid="backup-preset-select"><SelectValue placeholder="Apply a preset…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minimal">Minimal — 3 daily / 0 weekly / 0 monthly · 1 GB cap (test/sandbox)</SelectItem>
                    <SelectItem value="standard">Standard — 7 daily / 4 weekly / 3 monthly · 3 GB cap (recommended)</SelectItem>
                    <SelectItem value="extended">Extended — 14 daily / 8 weekly / 6 monthly · 8 GB cap (production)</SelectItem>
                    <SelectItem value="compliance">Compliance — 30 daily / 12 weekly / 24 monthly · 20 GB cap (audit/SOX)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-slate-500">Picking a preset fills the fields below — review then click <strong>Save Settings</strong>.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <Label>Schedule (UTC hour, 0-23)</Label>
                  <Input type="number" min={0} max={23} value={backupConfig.schedule_hour_utc} onChange={e => setBackupConfig(s => ({ ...s, schedule_hour_utc: e.target.value }))} data-testid="backup-schedule-hour" />
                  <p className="text-[11px] text-slate-500">Currently runs at {String(backupConfig.schedule_hour_utc).padStart(2, '0')}:00 UTC</p>
                </div>
                <div className="space-y-1">
                  <Label>Max Storage Size (MB)</Label>
                  <Input type="number" min={100} value={backupConfig.max_total_mb} onChange={e => setBackupConfig(s => ({ ...s, max_total_mb: e.target.value }))} data-testid="backup-max-mb" />
                  <p className="text-[11px] text-slate-500">Hard cap; oldest backups dropped first.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-slate-100 pt-3">
                <div className="space-y-1">
                  <Label>Keep daily backups</Label>
                  <Input type="number" min={0} value={backupConfig.keep_daily} onChange={e => setBackupConfig(s => ({ ...s, keep_daily: e.target.value }))} data-testid="backup-keep-daily" />
                </div>
                <div className="space-y-1">
                  <Label>Keep weekly (Sundays)</Label>
                  <Input type="number" min={0} value={backupConfig.keep_weekly} onChange={e => setBackupConfig(s => ({ ...s, keep_weekly: e.target.value }))} data-testid="backup-keep-weekly" />
                </div>
                <div className="space-y-1">
                  <Label>Keep monthly (1st)</Label>
                  <Input type="number" min={0} value={backupConfig.keep_monthly} onChange={e => setBackupConfig(s => ({ ...s, keep_monthly: e.target.value }))} data-testid="backup-keep-monthly" />
                </div>
              </div>
              <Button onClick={saveBackupConfig} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="backup-save-config">
                <Save className="w-4 h-4 mr-2" /> Save Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* iter77 — BOM Linked-File Templates */}
        <TabsContent value="bomtemplates" className="space-y-6" data-testid="bomtemplates-panel">
          <BomLinkedFileTemplatesPanel api={api} canEdit={user?.role === 'Admin' || user?.role === 'Engineer' || user?.role === 'ProductionSupervisor'} />
        </TabsContent>

        <TabsContent value="shipping" className="space-y-6" data-testid="shipping-panel">
          <ShippingPolicyPanel api={api} canEdit={user?.role === 'Admin'} />
        </TabsContent>

        {/* iter78 — Danger Zone (Admin only) */}
        {user?.role === 'Admin' && (
          <TabsContent value="danger" className="space-y-6" data-testid="danger-panel">
            <InboxRetentionPanel api={api} />
            <DangerZonePanel api={api} />
          </TabsContent>
        )}
      </Tabs>

      {/* Timecode Dialog (iter31) */}
      <Dialog open={timecodeDialogOpen} onOpenChange={setTimecodeDialogOpen}>
        <DialogContent data-testid="timecode-dialog">
          <DialogHeader><DialogTitle>{timecodeEditing ? 'Edit Timecode' : 'Add Timecode'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Label *</Label>
              <Input value={timecodeForm.label} onChange={e => setTimecodeForm({ ...timecodeForm, label: e.target.value })} placeholder="e.g., Bereavement, Jury Duty, Other Co – Acme" data-testid="timecode-label-input" />
              {!timecodeEditing && <p className="text-[11px] text-slate-400">Code will auto-slugify from label: <span className="font-mono">{(timecodeForm.label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}</span></p>}
            </div>
            <div className="space-y-1">
              <Label>Color</Label>
              <div className="flex flex-wrap gap-1">
                {COLOR_SWATCHES.map(c => (
                  <button key={c} type="button" onClick={() => setTimecodeForm({ ...timecodeForm, color: c })} className={`w-6 h-6 rounded ${c} ${timecodeForm.color === c ? 'ring-2 ring-orange-500 ring-offset-1' : ''}`} data-testid={`timecode-color-${c}`} />
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-center justify-between border border-slate-200 rounded-sm p-2">
                <div><Label className="text-sm">Payable</Label><p className="text-[11px] text-slate-500">Counts for payroll</p></div>
                <Switch checked={timecodeForm.is_payable} onCheckedChange={v => setTimecodeForm({ ...timecodeForm, is_payable: v })} data-testid="timecode-payable-switch" />
              </div>
              <div className="flex items-center justify-between border border-slate-200 rounded-sm p-2">
                <div><Label className="text-sm">Counts toward hours</Label><p className="text-[11px] text-slate-500">Include in weekly total</p></div>
                <Switch checked={timecodeForm.counts_toward_hours} onCheckedChange={v => setTimecodeForm({ ...timecodeForm, counts_toward_hours: v })} data-testid="timecode-counts-switch" />
              </div>
              <div className="flex items-center justify-between border border-slate-200 rounded-sm p-2">
                <div><Label className="text-sm">Requires WO</Label><p className="text-[11px] text-slate-500">Entry must link a work order</p></div>
                <Switch checked={timecodeForm.requires_work_order} onCheckedChange={v => setTimecodeForm({ ...timecodeForm, requires_work_order: v })} />
              </div>
              <div className="flex items-center justify-between border border-slate-200 rounded-sm p-2">
                <div><Label className="text-sm">Active</Label><p className="text-[11px] text-slate-500">Shown in timecard dropdown</p></div>
                <Switch checked={timecodeForm.active} onCheckedChange={v => setTimecodeForm({ ...timecodeForm, active: v })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Sort Order</Label>
              <Input type="number" value={timecodeForm.sort_order} onChange={e => setTimecodeForm({ ...timecodeForm, sort_order: parseInt(e.target.value) || 100 })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTimecodeDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveTimecode} className="bg-orange-500 hover:bg-orange-600" data-testid="timecode-save-btn">{timecodeEditing ? 'Save Changes' : 'Create Timecode'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Dialog (iter31) */}
      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader><DialogTitle>{userEditing ? 'Edit User' : 'Add User'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Email *</Label>
              <Input type="email" value={userForm.email} onChange={e => setUserForm({ ...userForm, email: e.target.value })} disabled={!!userEditing} data-testid="user-email-input" />
            </div>
            <div className="space-y-1">
              <Label>Full Name *</Label>
              <Input value={userForm.full_name} onChange={e => setUserForm({ ...userForm, full_name: e.target.value })} data-testid="user-name-input" />
            </div>
            <div className="space-y-1">
              <Label>Role</Label>
              <Select value={userForm.role} onValueChange={v => setUserForm({ ...userForm, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map(r => <SelectItem key={r.id} value={r.name}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!userEditing && (
              <div className="space-y-1">
                <Label>Initial Password *</Label>
                <Input type="password" value={userForm.password} onChange={e => setUserForm({ ...userForm, password: e.target.value })} placeholder="Min 8 chars" data-testid="user-password-input" />
              </div>
            )}
            <div className="flex items-center justify-between border border-slate-200 rounded-sm p-2">
              <div><Label>Active</Label><p className="text-[11px] text-slate-500">Disable to block login</p></div>
              <Switch checked={userForm.is_active} onCheckedChange={v => setUserForm({ ...userForm, is_active: v })} data-testid="user-active-switch" />
            </div>
            <div className="grid grid-cols-2 gap-3 pt-1 border-t border-slate-100">
              <div className="space-y-1">
                <Label>Birthday <span className="text-slate-400 text-[11px]">(MM-DD or full date)</span></Label>
                <Input
                  value={userForm.birthday || ''}
                  onChange={e => setUserForm({ ...userForm, birthday: e.target.value })}
                  placeholder="e.g., 03-21 or 1990-03-21"
                  data-testid="user-birthday-input"
                />
              </div>
              <div className="space-y-1">
                <Label>Hire Date</Label>
                <Input type="date" value={userForm.hire_date || ''} onChange={e => setUserForm({ ...userForm, hire_date: e.target.value })} data-testid="user-hire-date-input" />
              </div>
            </div>
            <p className="text-[11px] text-slate-500">These power the TV → Celebrations viewpoint (birthdays + work anniversaries).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUserDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveUser} className="bg-orange-500 hover:bg-orange-600" data-testid="user-save-btn">{userEditing ? 'Save Changes' : 'Create User'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Role Dialog (iter31) — permissions matrix */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="role-dialog">
          <DialogHeader><DialogTitle>{roleEditing ? `Edit Role — ${roleEditing.name}` : 'Add Role'}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Role Name *</Label>
                <Input value={roleForm.name} onChange={e => setRoleForm({ ...roleForm, name: e.target.value })} disabled={!!(roleEditing && roleEditing.is_system)} data-testid="role-name-input" />
                {roleEditing?.is_system && <p className="text-[11px] text-slate-400">System role name is locked</p>}
              </div>
              <div className="space-y-1">
                <Label>Description</Label>
                <Input value={roleForm.description} onChange={e => setRoleForm({ ...roleForm, description: e.target.value })} data-testid="role-desc-input" />
              </div>
            </div>
            <div className="pt-2">
              <Label className="text-sm">Permissions Matrix</Label>
              <p className="text-[11px] text-slate-400 mb-2">Pick the actions allowed per module. Admin role is always full-access.</p>
              <div className="border border-slate-200 rounded-sm overflow-x-auto">
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Module</TableHead>
                      {(permModules.actions || []).map(a => (
                        <TableHead key={a} className="text-center capitalize">{a}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(permModules.modules || []).map(m => (
                      <TableRow key={m.key}>
                        <TableCell className="font-medium text-sm">{m.label}</TableCell>
                        {(permModules.actions || []).map(a => (
                          <TableCell key={a} className="text-center">
                            <input
                              type="checkbox"
                              checked={(roleForm.permissions[m.key] || []).includes(a)}
                              onChange={() => togglePerm(m.key, a)}
                              disabled={roleEditing?.name === 'Admin'}
                              className="w-4 h-4 accent-orange-500"
                              data-testid={`perm-${m.key}-${a}`}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <div className="flex items-center justify-between border border-slate-200 rounded-sm p-2">
              <div><Label>Active</Label><p className="text-[11px] text-slate-500">Disable to prevent assignment</p></div>
              <Switch checked={roleForm.active} onCheckedChange={v => setRoleForm({ ...roleForm, active: v })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveRole} className="bg-orange-500 hover:bg-orange-600" data-testid="role-save-btn">{roleEditing ? 'Save Changes' : 'Create Role'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Serial Config Dialog */}
      <Dialog open={serialDialogOpen} onOpenChange={setSerialDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Serial Number Configuration</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Configuration Name *</Label>
              <Input
                value={newSerialConfig.config_name}
                onChange={(e) => setNewSerialConfig({ ...newSerialConfig, config_name: e.target.value })}
                placeholder="e.g., Electronics, HVAC, Final Product"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prefix *</Label>
                <Input
                  value={newSerialConfig.prefix}
                  onChange={(e) => setNewSerialConfig({ ...newSerialConfig, prefix: e.target.value.toUpperCase() })}
                  placeholder="e.g., ELEC, HVAC, FP"
                />
              </div>
              <div className="space-y-2">
                <Label>Suffix (optional)</Label>
                <Input
                  value={newSerialConfig.suffix}
                  onChange={(e) => setNewSerialConfig({ ...newSerialConfig, suffix: e.target.value.toUpperCase() })}
                  placeholder="e.g., A, REV1"
                />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label>Include Date</Label>
                <p className="text-xs text-slate-500">Add YYYYMMDD to serial number</p>
              </div>
              <Switch
                checked={newSerialConfig.include_date}
                onCheckedChange={(v) => setNewSerialConfig({ ...newSerialConfig, include_date: v })}
              />
            </div>
            <div className="space-y-2">
              <Label>Number Padding (digits)</Label>
              <Select 
                value={String(newSerialConfig.padding_digits)} 
                onValueChange={(v) => setNewSerialConfig({ ...newSerialConfig, padding_digits: parseInt(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3 digits (001)</SelectItem>
                  <SelectItem value="4">4 digits (0001)</SelectItem>
                  <SelectItem value="5">5 digits (00001)</SelectItem>
                  <SelectItem value="6">6 digits (000001)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-slate-50 p-3 rounded-sm">
              <Label className="text-xs text-slate-500">Preview</Label>
              <p className="font-mono mt-1">
                {newSerialConfig.prefix || 'XXX'}-
                {newSerialConfig.include_date ? '20250126-' : ''}
                {'0'.repeat(newSerialConfig.padding_digits - 1)}1
                {newSerialConfig.suffix ? `-${newSerialConfig.suffix}` : ''}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSerialDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={async () => {
                if (!newSerialConfig.config_name || !newSerialConfig.prefix) {
                  toast.error('Name and prefix are required');
                  return;
                }
                try {
                  await api().post('/serial-configs', newSerialConfig);
                  toast.success('Serial config created');
                  setSerialDialogOpen(false);
                  setNewSerialConfig({
                    config_name: '',
                    prefix: '',
                    suffix: '',
                    include_date: true,
                    date_format: '%Y%m%d',
                    padding_digits: 5
                  });
                  fetchAllSettings();
                } catch (error) {
                  toast.error('Failed to create config');
                }
              }}
              className="bg-orange-500 hover:bg-orange-600"
            >
              Create Config
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter52: Reject pending user dialog */}
      <Dialog open={!!rejectingUser} onOpenChange={(o) => !o && setRejectingUser(null)}>
        <DialogContent data-testid="reject-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-600">Reject Account Request</DialogTitle>
            <DialogDescription>
              {rejectingUser?.full_name} · {rejectingUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Reason (optional, included in rejection email)</Label>
            <Textarea
              data-testid="reject-user-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="e.g. Not a current employee · already has an account · please contact your manager…"
              className="rounded-sm"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectingUser(null)} data-testid="reject-user-cancel">Cancel</Button>
            <Button
              onClick={submitReject}
              className="bg-red-600 hover:bg-red-700"
              data-testid="reject-user-confirm"
            >
              Reject Access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter59: TV Station dialog */}
      <Dialog open={stationDialogOpen} onOpenChange={setStationDialogOpen}>
        <DialogContent className="max-w-3xl" data-testid="station-dialog">
          <DialogHeader>
            <DialogTitle>{stationDraft.id ? 'Edit TV Station' : 'New TV Station'}</DialogTitle>
            <DialogDescription>Pre-configure the rotation, defaults, and alerts for one TV. Each TV opens <code>/tv?station=&lt;id&gt;</code> and inherits this config automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Station Name *</Label>
                <Input value={stationDraft.name} onChange={e => setStationDraft(d => ({ ...d, name: e.target.value }))} placeholder="e.g., Production Cell A" data-testid="station-name-input" />
              </div>
              <div className="space-y-1">
                <Label>Description (optional)</Label>
                <Input value={stationDraft.description} onChange={e => setStationDraft(d => ({ ...d, description: e.target.value }))} placeholder="e.g., 65-inch TV at the welding bay" data-testid="station-description-input" />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Viewpoint Rotation (drag-style buttons; click to add/remove)</Label>
              <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                <p className="text-[11px] text-slate-500 mb-2 uppercase tracking-widest">Available</p>
                <div className="flex flex-wrap gap-1.5">
                  {TV_VIEWPOINT_OPTIONS.map(o => {
                    const has = stationDraft.viewpoints.includes(o.key);
                    return (
                      <button
                        key={o.key}
                        type="button"
                        onClick={() => toggleViewpoint(o.key)}
                        className={`px-2.5 py-1 text-xs rounded-sm border ${has ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-500'}`}
                        data-testid={`station-vp-toggle-${o.key}`}
                      >
                        {has ? '✓ ' : '+ '}{o.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 mt-3 mb-2 uppercase tracking-widest">In Rotation ({stationDraft.viewpoints.length})</p>
                {stationDraft.viewpoints.length === 0 ? (
                  <p className="text-xs text-slate-500 italic">Pick at least one viewpoint above.</p>
                ) : (
                  <ol className="space-y-1">
                    {stationDraft.viewpoints.map((v, i) => {
                      const opt = TV_VIEWPOINT_OPTIONS.find(o => o.key === v);
                      return (
                        <li key={v} className="flex items-center gap-2 bg-white border border-slate-200 rounded-sm px-2 py-1.5 text-sm">
                          <span className="text-slate-400 font-mono text-xs w-6">#{i + 1}</span>
                          <span className="flex-1">{opt?.label || v}</span>
                          <button type="button" onClick={() => moveViewpoint(v, -1)} disabled={i === 0} className="text-xs px-1.5 py-0.5 disabled:opacity-30 hover:bg-slate-100 rounded-sm" data-testid={`station-vp-up-${v}`}>↑</button>
                          <button type="button" onClick={() => moveViewpoint(v, 1)} disabled={i === stationDraft.viewpoints.length - 1} className="text-xs px-1.5 py-0.5 disabled:opacity-30 hover:bg-slate-100 rounded-sm" data-testid={`station-vp-down-${v}`}>↓</button>
                          <button type="button" onClick={() => toggleViewpoint(v)} className="text-xs text-red-500 px-1.5 py-0.5 hover:bg-red-50 rounded-sm" data-testid={`station-vp-remove-${v}`}>×</button>
                        </li>
                      );
                    })}
                  </ol>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label>Rotation Seconds</Label>
                <Input type="number" min={5} max={600} value={stationDraft.rotation_seconds} onChange={e => setStationDraft(d => ({ ...d, rotation_seconds: e.target.value }))} data-testid="station-rotation-seconds" />
              </div>
              <div className="space-y-1">
                <Label>Default Period</Label>
                <Select value={stationDraft.default_period} onValueChange={v => setStationDraft(d => ({ ...d, default_period: v }))}>
                  <SelectTrigger data-testid="station-default-period"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Lock to Product Type</Label>
                <Select value={stationDraft.product_type_id || '__all__'} onValueChange={v => setStationDraft(d => ({ ...d, product_type_id: v === '__all__' ? '' : v }))}>
                  <SelectTrigger data-testid="station-product-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All products</SelectItem>
                    {stationProductTypes.filter(p => p.active_status !== false).map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.product_type_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4 border-t border-slate-100 pt-3">
              <div className="flex items-center gap-2">
                <Switch checked={!!stationDraft.audio_alerts} onCheckedChange={v => setStationDraft(d => ({ ...d, audio_alerts: v }))} data-testid="station-audio-alerts" />
                <Label className="!m-0 text-sm">Audio chime on red</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!stationDraft.visual_alerts} onCheckedChange={v => setStationDraft(d => ({ ...d, visual_alerts: v }))} data-testid="station-visual-alerts" />
                <Label className="!m-0 text-sm">Visual flash on red</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!stationDraft.is_active} onCheckedChange={v => setStationDraft(d => ({ ...d, is_active: v }))} data-testid="station-is-active" />
                <Label className="!m-0 text-sm">Active</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStationDialogOpen(false)} data-testid="station-cancel">Cancel</Button>
            <Button onClick={saveStation} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="station-save">
              <Save className="w-4 h-4 mr-2" /> Save Station
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* iter55: Company News dialog */}
      <Dialog open={newsDialogOpen} onOpenChange={setNewsDialogOpen}>
        <DialogContent className="max-w-2xl" data-testid="news-dialog">
          <DialogHeader>
            <DialogTitle>{newsDraft.id ? 'Edit Company News' : 'New Company News'}</DialogTitle>
            <DialogDescription>This will appear on the TV → Company News viewpoint.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Title *</Label>
              <Input value={newsDraft.title} onChange={e => setNewsDraft(d => ({ ...d, title: e.target.value }))} placeholder="e.g., Q1 town hall — Friday at 2 PM" data-testid="news-title-input" />
            </div>
            <div className="space-y-1">
              <Label>Body</Label>
              <RichTextEditor
                testId="news-body-editor"
                value={newsDraft.body || ''}
                onChange={(html) => setNewsDraft(d => ({ ...d, body: html }))}
                placeholder="Write the announcement…"
                variables={[]}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Starts (optional)</Label>
                <Input type="date" value={newsDraft.starts_at || ''} onChange={e => setNewsDraft(d => ({ ...d, starts_at: e.target.value }))} data-testid="news-starts-at" />
              </div>
              <div className="space-y-1">
                <Label>Ends (optional)</Label>
                <Input type="date" value={newsDraft.ends_at || ''} onChange={e => setNewsDraft(d => ({ ...d, ends_at: e.target.value }))} data-testid="news-ends-at" />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Image (optional, ≤6 MB)</Label>
              <div className="flex items-center gap-3">
                <div className="w-32 h-20 border border-slate-200 rounded-sm flex items-center justify-center overflow-hidden bg-slate-50">
                  {newsDraft.image_data
                    ? <img src={newsDraft.image_data} alt="preview" className="max-w-full max-h-full object-contain" />
                    : <span className="text-[10px] uppercase text-slate-400">No image</span>}
                </div>
                <Input type="file" accept="image/*" onChange={onNewsImage} className="rounded-sm" data-testid="news-image-input" />
                {newsDraft.image_data && (
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setNewsDraft(d => ({ ...d, image_data: '' }))}>Remove</Button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-6 pt-1">
              <div className="flex items-center gap-2">
                <Switch checked={!!newsDraft.pinned} onCheckedChange={v => setNewsDraft(d => ({ ...d, pinned: v }))} data-testid="news-pinned" />
                <Label className="!m-0 text-sm flex items-center gap-1"><Pin className="w-3 h-3" /> Pinned</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={!!newsDraft.show_on_tv} onCheckedChange={v => setNewsDraft(d => ({ ...d, show_on_tv: v }))} data-testid="news-show-on-tv" />
                <Label className="!m-0 text-sm">Show on TV</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewsDialogOpen(false)} data-testid="news-cancel">Cancel</Button>
            <Button onClick={saveNews} disabled={saving} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="news-save">
              <Save className="w-4 h-4 mr-2" /> Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};


// iter58: Brand Preview Card — shows logo + colors + email + report rendered live
const BrandPreviewCard = ({ form, fallbackLogo, api }) => {
  const [emailHtml, setEmailHtml] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const logo = form.logo_data || fallbackLogo || '';
  const tvLogo = form.tv_logo_data || logo;
  const appName = form.app_name || form.fallback_app_name || 'BOM Maestro';
  const tagline = form.tagline || form.fallback_tagline || '';
  const primary = form.primary_color || '#ea580c';
  const primaryHover = form.primary_color_hover || primary;

  const reloadEmail = async () => {
    setEmailLoading(true);
    try {
      const r = await api().get('/admin/branding-settings/email-preview');
      setEmailHtml(r.data?.html || '');
    } catch {
      setEmailHtml('<p style="color:#ef4444;padding:1rem;">Could not load email preview. Save Branding first to see the latest config rendered.</p>');
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <Card className="industrial-card" data-testid="branding-preview-card">
      <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg flex items-center gap-2"><Eye className="w-5 h-5" /> Brand Preview</CardTitle>
          <p className="text-xs text-slate-500 mt-1">See exactly how your logo + colors render across the app. Changes here only show after you click <b>Save Branding</b>.</p>
        </div>
        <Button variant="outline" size="sm" onClick={reloadEmail} disabled={emailLoading} className="rounded-sm" data-testid="branding-preview-reload-email">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${emailLoading ? 'animate-spin' : ''}`} /> Refresh email
        </Button>
      </CardHeader>
      <CardContent className="p-4 space-y-6">
        {/* 1) Top bar preview */}
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-slate-500">1 · App Top Bar</p>
          <div className="border border-slate-200 rounded-sm overflow-hidden bg-white" data-testid="branding-preview-topbar">
            <div className="h-14 px-4 flex items-center gap-4 border-b border-slate-200">
              <div className="flex items-center gap-2 mr-auto">
                {logo ? (
                  <img src={logo} alt={appName} className="h-7 w-auto object-contain" />
                ) : (
                  <div className="w-7 h-7 rounded-sm flex items-center justify-center text-white font-bold" style={{ background: primary }}>{appName.slice(0, 1)}</div>
                )}
                <span className="font-bold text-sm">{appName}</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-7 h-7 bg-slate-100 rounded-sm" />
                <div className="w-7 h-7 bg-slate-100 rounded-sm" />
                <button className="px-3 py-1 rounded-sm text-xs text-white font-medium" style={{ background: primary }} onMouseEnter={(e) => e.currentTarget.style.background = primaryHover} onMouseLeave={(e) => e.currentTarget.style.background = primary}>
                  Primary action
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* 2) Login splash preview */}
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-slate-500">2 · Login Splash</p>
          <div className="border border-slate-200 rounded-sm overflow-hidden grid grid-cols-2 h-48" data-testid="branding-preview-login">
            <div className="relative bg-slate-900 text-white p-4 flex flex-col justify-center" style={{ borderRight: `4px solid ${primary}` }}>
              <div className="flex items-center gap-2 mb-2">
                {logo && <img src={logo} alt="" className="h-8 w-auto object-contain bg-white/10 p-1 rounded-sm" />}
                <span className="font-bold text-lg">{appName}</span>
              </div>
              {tagline && <p className="text-xs text-slate-300">{tagline}</p>}
              <p className="text-[10px] text-slate-500 mt-auto">© 2026 {appName}</p>
            </div>
            <div className="bg-white p-4 flex flex-col justify-center text-xs">
              <p className="font-bold mb-2">Sign in</p>
              <div className="border border-slate-200 rounded-sm px-2 py-1 mb-1.5 text-slate-400">you@company.com</div>
              <div className="border border-slate-200 rounded-sm px-2 py-1 mb-2 text-slate-400">password</div>
              <button className="rounded-sm text-white py-1.5 text-xs font-medium" style={{ background: primary }}>Sign in</button>
            </div>
          </div>
        </div>

        {/* 3) TV preview */}
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-slate-500">3 · Shop-Floor TV</p>
          <div className="border border-slate-200 rounded-sm overflow-hidden bg-slate-950 text-white p-4" data-testid="branding-preview-tv">
            <div className="flex items-center gap-3 pb-2 border-b" style={{ borderColor: primary }}>
              {tvLogo && <img src={tvLogo} alt="" className="h-8 w-auto object-contain" />}
              <span className="font-bold text-base">{appName}</span>
              <span className="ml-auto text-xs text-slate-400">PRODUCTION FLOOR</span>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2">
              <div className="rounded-sm p-3 text-center" style={{ background: primary }}>
                <p className="text-[10px] uppercase tracking-widest opacity-80">On Pace</p>
                <p className="font-mono font-black text-2xl">82%</p>
              </div>
              <div className="rounded-sm p-3 text-center bg-slate-900">
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Built Today</p>
                <p className="font-mono font-bold text-2xl text-emerald-400">14</p>
              </div>
              <div className="rounded-sm p-3 text-center bg-slate-900">
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Open Recalls</p>
                <p className="font-mono font-bold text-2xl">0</p>
              </div>
            </div>
          </div>
        </div>

        {/* 4) Email preview */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-widest text-slate-500">4 · Transactional Email</p>
            {!emailHtml && (
              <Button size="sm" variant="ghost" onClick={reloadEmail} className="text-xs h-7" data-testid="branding-preview-email-load">
                Load preview
              </Button>
            )}
          </div>
          <div className="border border-slate-200 rounded-sm bg-white overflow-hidden" data-testid="branding-preview-email">
            {emailHtml ? (
              <div className="p-4 max-h-96 overflow-y-auto" dangerouslySetInnerHTML={{ __html: emailHtml }} />
            ) : (
              <div className="p-8 text-center text-sm text-slate-500">Click <b>Load preview</b> to render a sample email with your current branding.</div>
            )}
          </div>
          <p className="text-[11px] text-slate-500">This is the exact wrapper used for every Resend email — registration, recall notifications, RFQs, etc.</p>
        </div>

        {/* 5) Printable report preview */}
        <div className="space-y-2">
          <p className="text-xs uppercase tracking-widest text-slate-500">5 · Printable Report Header</p>
          <div className="border border-slate-200 rounded-sm bg-white p-6" data-testid="branding-preview-report">
            <div className="flex items-start justify-between pb-3 border-b border-slate-300">
              <div>
                <h3 className="text-xl font-bold tracking-tight">{appName}</h3>
                {tagline && <p className="text-xs text-slate-500 mt-0.5">{tagline}</p>}
              </div>
              {logo && <img src={logo} alt="" className="h-12 w-auto object-contain" />}
            </div>
            <h4 className="text-sm font-bold mt-3 uppercase tracking-widest" style={{ color: primary }}>Inventory Health Report</h4>
            <p className="text-[11px] text-slate-500">As of {new Date().toLocaleDateString()} · Generated by {appName}</p>
            <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
              <div className="border border-slate-200 rounded-sm p-2"><div className="text-slate-500 text-[10px] uppercase">Active</div><div className="font-mono font-bold">142</div></div>
              <div className="border border-slate-200 rounded-sm p-2"><div className="text-slate-500 text-[10px] uppercase">Low Stock</div><div className="font-mono font-bold text-amber-600">7</div></div>
              <div className="border border-slate-200 rounded-sm p-2"><div className="text-slate-500 text-[10px] uppercase">Out</div><div className="font-mono font-bold text-red-600">2</div></div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// iter54: file upload row for branding images
const BrandImageRow = ({ label, field, preview, onChange, onClear, testid, dark = false }) => (
  <div className="space-y-1.5">
    <Label className="text-xs">{label}</Label>
    <div className="flex items-center gap-3">
      <div className={`w-32 h-16 border border-slate-200 rounded-sm flex items-center justify-center overflow-hidden ${dark ? 'bg-slate-900' : 'bg-slate-50'}`} data-testid={`branding-preview-${testid}`}>
        {preview ? (
          <img src={preview} alt="preview" className="max-w-full max-h-full object-contain" />
        ) : (
          <span className={`text-[10px] uppercase ${dark ? 'text-slate-500' : 'text-slate-400'}`}>No image</span>
        )}
      </div>
      <div className="flex flex-col gap-1.5 flex-1">
        <label className="cursor-pointer inline-flex items-center justify-center px-3 py-1.5 text-xs border border-slate-200 rounded-sm hover:bg-slate-50 w-fit" data-testid={`branding-upload-${testid}-label`}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Upload
          <input
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden"
            data-testid={`branding-upload-${testid}`}
            onChange={(e) => onChange(field, e)}
          />
        </label>
        {preview && (
          <button
            type="button"
            onClick={onClear}
            className="text-[11px] text-red-600 hover:underline w-fit"
            data-testid={`branding-clear-${testid}`}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  </div>
);

// iter52: helper component for a single pending approval row
const PendingApprovalRow = ({ user, busy, onApprove, onReject }) => {
  // Parse "Name — Requesting RoleName: reason" out of full_name (set by signup)
  const m = (user.full_name || '').match(/^(.*?) — Requesting ([A-Za-z]+)(?::\s*(.*))?$/);
  const displayName = m ? m[1] : user.full_name;
  const requestedRole = m ? m[2] : 'Operator';
  const requestedReason = m ? (m[3] || '').trim() : '';
  const [roleChoice, setRoleChoice] = React.useState(requestedRole);
  const submitted = user.created_at ? new Date(user.created_at).toLocaleString() : '—';
  return (
    <TableRow data-testid={`pending-user-row-${user.id}`}>
      <TableCell>
        <div className="font-medium">{displayName}</div>
        <div className="text-xs text-slate-500">Requested: <strong>{requestedRole}</strong></div>
        {requestedReason && (
          <div className="text-xs text-slate-500 mt-0.5 italic">"{requestedReason}"</div>
        )}
      </TableCell>
      <TableCell className="text-sm">{user.email}</TableCell>
      <TableCell>
        <Select value={roleChoice} onValueChange={setRoleChoice}>
          <SelectTrigger data-testid={`pending-role-select-${user.id}`} className="rounded-sm h-8 text-xs w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Admin">Admin</SelectItem>
            <SelectItem value="InventoryManager">Inventory Manager</SelectItem>
            <SelectItem value="ProductionSupervisor">Production Supervisor</SelectItem>
            <SelectItem value="Purchasing">Purchasing</SelectItem>
            <SelectItem value="Operator">Operator</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell className="text-xs text-slate-500">{submitted}</TableCell>
      <TableCell className="text-right whitespace-nowrap">
        <Button
          size="sm"
          onClick={() => onApprove(user, roleChoice)}
          disabled={busy}
          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm h-7 text-xs mr-1"
          data-testid={`approve-user-${user.id}`}
        >
          Approve
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={onReject}
          disabled={busy}
          className="rounded-sm h-7 text-xs text-red-600 border-red-200 hover:bg-red-50"
          data-testid={`reject-user-${user.id}`}
        >
          Reject
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default AdminSettingsPage;
